// Pixel-art room builder (DOM-free). Rooms are drawn on the same pixel grid as the avatars:
// one art pixel = SPRITE_SCALE world pixels, with 2:1 isometric lines and dark outlines.
import { DEFAULT_FLOOR, DEFAULT_WALL, FLOOR_BY_ID, MAX_GRID, WALL_BY_ID, type Furni } from "../shared/protocol";
import { Layer, T, bounds, composite, crop, hex, mix, ramp, type Bitmap, type RGB } from "./pixel";

export const P = 2; // world px per art px (matches SPRITE_SCALE)
export const WALL_PX = 55; // wall height in art px (WALL_H / P)
export const OX = MAX_GRID * 16 + 10; // art-space position of tile (0,0)'s top corner
export const OY = WALL_PX + 22;
export const RW = MAX_GRID * 32 + 20;
export const RH = OY + MAX_GRID * 16 + 14;

type Pt = [number, number];
/** Grid coords + height (art px) -> art pixel coords. */
export const ip = (gx: number, gy: number, z = 0): Pt => [OX + (gx - gy) * 16, OY + (gx + gy) * 8 - z];

const L = (pal: RGB[], outline?: RGB | null) => new Layer(RW, RH, pal, outline);

/** Axis-aligned iso box over tiles [x,x+w) x [y,y+h), from height z0 to z0+H (art px). */
function box(Ly: Layer, x: number, y: number, w: number, h: number, z0: number, H: number, top: number = T.BASE) {
  const A = (z: number) => ip(x, y, z), B = (z: number) => ip(x + w, y, z), C = (z: number) => ip(x + w, y + h, z), D = (z: number) => ip(x, y + h, z);
  Ly.poly([D(z0), C(z0), C(z0 + H), D(z0 + H)], T.SH); // +y face (front left)
  Ly.poly([B(z0), C(z0), C(z0 + H), B(z0 + H)], T.DEEP); // +x face (front right)
  Ly.poly([A(z0 + H), B(z0 + H), C(z0 + H), D(z0 + H)], top);
  return Ly;
}

const diamond = (x: number, y: number, w: number, h: number, z = 0): Pt[] => [ip(x, y, z), ip(x + w, y, z), ip(x + w, y + h, z), ip(x, y + h, z)];

/** Rectangle on the back wall (along y=0) or the left wall (along x=0). */
function wallRect(wall: "back" | "left", a: number, b: number, z0: number, z1: number): Pt[] {
  return wall === "back" ? [ip(a, 0, z0), ip(b, 0, z0), ip(b, 0, z1), ip(a, 0, z1)] : [ip(0, a, z0), ip(0, b, z0), ip(0, b, z1), ip(0, a, z1)];
}

// ---------------------------------------------------------------- background

/** Walls and floor of a size x size room. Furniture, rugs included, is drawn separately. */
export function buildBackground(wallId: string, floorId: string, size: number): Bitmap {
  const wd = WALL_BY_ID[wallId] ?? WALL_BY_ID[DEFAULT_WALL];
  const fd = FLOOR_BY_ID[floorId] ?? FLOOR_BY_ID[DEFAULT_FLOOR];
  const th = { wall: wd.color, wallSide: wd.side, floorA: fd.a, floorB: fd.b };
  const G = size;
  const layers: Layer[] = [];

  // walls
  const wall = L(ramp(th.wall, [th.wallSide]));
  wall.poly(wallRect("back", 0, G, 0, WALL_PX), T.BASE);
  wall.poly(wallRect("left", 0, G, 0, WALL_PX), T.A);
  // wallpaper patterns
  const sidePal = ramp(th.wallSide);
  wall.pal[T.B] = sidePal[T.SH];
  wall.pal[T.C] = sidePal[T.HI];
  for (let y = 0; y < RH; y++)
    for (let x = 0; x < RW; x++) {
      const v = wall.get(x, y);
      if (!v) continue;
      const left = v === T.A;
      const u = left ? y + (x - OX) * 0.5 : y - (x - OX) * 0.5; // height along the wall face
      const along = x;
      const ru = Math.round(u);
      let mark = false;
      if (wd.pattern === "dots") mark = (ru % 12 === 0 && along % 24 === (Math.round(u / 12) % 2) * 12) || (ru % 12 === 6 && along % 24 === 6 + (Math.round(u / 12) % 2) * 12);
      else if (wd.pattern === "brick") { const row = Math.floor(u / 6); mark = ru % 6 === 0 || (along + row * 8) % 16 === 0; }
      else if (wd.pattern === "lines") mark = ru % 10 === 0 && along % 2 === 0;
      else if (wd.pattern === "grid") mark = ru % 8 === 0 || along % 16 === 0;
      else if (wd.pattern === "stripes") mark = along % 12 < 3;
      else if (wd.pattern === "tile") { const row = Math.floor(u / 4); mark = ru % 4 === 0 || (along + (row % 2) * 4) % 8 === 0; }
      else if (wd.pattern === "panel") { const pu = ((ru % 24) + 24) % 24, pa = along % 32; mark = pu === 0 || pa === 0 || ((pu === 3 || pu === 21) && (pa === 3 || pa === 29)); }
      if (mark) wall.set(x, y, left ? T.B : T.SH);
    }
  // baseboard and top trim
  wall.poly(wallRect("back", 0, G, 0, 4), T.DEEP).poly(wallRect("left", 0, G, 0, 4), T.DEEP);
  wall.poly([ip(0, 0, WALL_PX), ip(G, 0, WALL_PX), ip(G, -0.3, WALL_PX), ip(-0.3, -0.3, WALL_PX)], T.HI);
  wall.poly([ip(0, 0, WALL_PX), ip(0, G, WALL_PX), ip(-0.3, G, WALL_PX), ip(-0.3, -0.3, WALL_PX)], T.HI);
  wall.poly([ip(G, 0, 0), ip(G, -0.3, 0), ip(G, -0.3, WALL_PX), ip(G, 0, WALL_PX)], T.SH);
  wall.poly([ip(0, G, 0), ip(-0.3, G, 0), ip(-0.3, G, WALL_PX), ip(0, G, WALL_PX)], T.DEEP);
  layers.push(wall);

  // floor slab edge
  const edge = L(ramp(th.floorA));
  edge.poly([ip(0, G), ip(G, G), [ip(G, G)[0], ip(G, G)[1] + 6], [ip(0, G)[0], ip(0, G)[1] + 6]], T.SH);
  edge.poly([ip(G, 0), ip(G, G), [ip(G, G)[0], ip(G, G)[1] + 6], [ip(G, 0)[0], ip(G, 0)[1] + 6]], T.DEEP);
  layers.push(edge);

  // checker floor
  const floor = L(ramp(th.floorA, [th.floorB]));
  for (let gy = 0; gy < G; gy++) for (let gx = 0; gx < G; gx++) floor.poly(diamond(gx, gy, 1, 1), (gx + gy) % 2 ? T.BASE : T.A);
  // soft shadow where the floor meets the walls
  for (let y = 0; y < RH; y++) for (let x = 0; x < RW; x++) if (floor.get(x, y) && wall.get(x, y - 1) && !floor.get(x, y - 1)) floor.set(x, y, T.SH);
  for (let y = 0; y < RH; y++) for (let x = 0; x < RW; x++) if (floor.get(x, y) && (!floor.get(x, y - 1) || !floor.get(x, y - 2)) && wall.get(x, y - 2)) floor.set(x, y, T.SH);
  floor.outline = null;
  layers.push(floor);

  return composite(RW, RH, layers);
}

/** A shape stamped flat onto a wall at a point (stars and hearts on posters and signs). */
function stampAt(Ly: Layer, [cx, cy]: Pt, rows: string[], v: number) {
  Ly.stamp(cx - (rows[0].length >> 1), cy - (rows.length >> 1), rows, { x: v });
}
const STAR = ["....x....", "....x....", "...xxx...", "xxxxxxxxx", ".xxxxxxx.", "..xxxxx..", "..xx.xx..", ".xx...xx.", ".x.....x."];
const HEART = [".xx.xx.", "xxxxxxx", "xxxxxxx", ".xxxxx.", "..xxx..", "...x..."];

/**
 * Stamps a print flat along a wall, skewed with it. Columns run left to right on screen on both
 * walls, so lettering never reads backwards on the left wall. (a, z) is the print's top-left.
 */
function wallStamp(Ly: Layer, left: boolean, s0: number, a: number, z: number, rows: string[], key: Record<string, number>) {
  const w = Math.max(...rows.map((r) => r.length));
  rows.forEach((row, j) => {
    for (let i = 0; i < row.length; i++) {
      const v = key[row[i]];
      if (!v) continue;
      const [px, py] = left ? ip(0, s0 + a + (w - 1 - i) / 16, z - j) : ip(s0 + a + i / 16, 0, z - j);
      Ly.set(Math.round(px), Math.round(py), v);
    }
  });
}
const FONT: Record<string, string[]> = {
  X: ["x...x", "xx.xx", ".xxx.", "..x..", ".xxx.", "xx.xx", "x...x"],
  R: ["xxxx.", "xx.xx", "xx.xx", "xxxx.", "xx.x.", "xx.xx", "xx.xx"],
  O: [".xxx.", "xx.xx", "xx.xx", "xx.xx", "xx.xx", "xx.xx", ".xxx."],
  M: ["x...x", "xx.xx", "xxxxx", "xx.xx", "xx.xx", "xx.xx", "xx.xx"],
  S: [".xxxx", "xx...", "xx...", ".xxx.", "...xx", "...xx", "xxxx."],
};
const word = (text: string, gap = 1) => FONT.X.map((_, j) => [...text].map((ch) => FONT[ch][j]).join(".".repeat(gap)));

/** Pieces hung on a wall, drawn along the wall they're on. a and b run along the wall from the piece's start. */
function wallPiece(f: Furni): Layer[] | null {
  const left = f.wall === 1;
  const s0 = left ? f.y : f.x;
  const R = (a: number, b: number, z0: number, z1: number) => wallRect(left ? "left" : "back", s0 + a, s0 + b, z0, z1);
  const at = (a: number, z: number): Pt => (left ? ip(0, s0 + a, z) : ip(s0 + a, 0, z));
  switch (f.kind) {
    case "window": {
      const w = L(ramp(f.color, ["#9ad4ff", "#ffffff", "#c8e8ff"]));
      w.poly(R(0, 3, 14, 44), T.BASE).poly(R(0.2, 2.8, 17, 41), T.A);
      w.poly(R(0.6, 1.6, 30, 33), T.B).poly(R(0.8, 1.3, 33, 35), T.B); // cloud
      w.poly(R(1.45, 1.55, 17, 41), T.BASE).poly(R(0.2, 2.8, 28.5, 29.5), T.BASE);
      return [w];
    }
    case "sunset": {
      const w = L(ramp(f.color, ["#ff7e5f", "#feb47b", "#ffd36b", "#ffe9a8"]));
      w.poly(R(0, 3, 12, 48), T.BASE);
      const bands = [T.A, T.A, T.B, T.B, T.C, T.W];
      for (let i = 0; i < 6; i++) w.poly(R(0.15, 2.85, 15 + i * 5.3, 15 + (i + 1) * 5.3 + 0.5), bands[5 - i]);
      w.pal[T.W] = hex("#ffe9a8");
      const [sx, sy] = at(1.5, 22);
      w.ellipse(sx - 5, sy - 3, sx + 5, sy + 3, T.W);
      return [w];
    }
    case "starposter": {
      const p = L(ramp(f.color, ["#ffe14a", "#ffffff"]));
      p.poly(R(0.2, 1.8, 22, 46), T.BASE);
      stampAt(p, at(1, 34), STAR, T.A);
      return [p];
    }
    case "heartposter": {
      const p = L(ramp(f.color, ["#ff4f8b", "#ffffff", "#c9a0ff"]));
      p.poly(R(0.25, 1.75, 24, 44), T.BASE).poly(R(0.25, 1.75, 24, 30), T.C);
      stampAt(p, at(1, 37), HEART, T.A);
      return [p];
    }
    case "stripeposter": {
      const p = L(ramp(f.color, ["#ff3d8b", "#21d4fd"]));
      p.poly(R(0, 2, 20, 44), T.BASE);
      p.poly(R(0.2, 1.8, 26, 28), T.A).poly(R(0.2, 1.8, 32, 34), T.B).poly(R(0.2, 1.8, 38, 40), T.A);
      return [p];
    }
    case "neonsign": {
      const n = L(ramp("#1a1430", [f.color, "#ff3d8b", "#b4ff39"]));
      n.poly(R(0, 4, 18, 46), T.BASE).poly(R(0.15, 3.85, 20, 44), T.A).poly(R(0.3, 3.7, 22, 42), T.BASE);
      stampAt(n, at(1, 32), STAR, T.B);
      stampAt(n, at(3, 32), HEART, T.C);
      return [n];
    }
    case "frame": {
      // a painting: hills under a sunny sky, in a gold or chrome frame
      const p = L(ramp(f.color, ["#9ad4ff", "#5aa85a", "#ffd23f", "#3f8a4a"]));
      p.poly(R(0.15, 1.85, 20, 46), T.BASE).poly(R(0.3, 1.7, 23, 43), T.A);
      p.poly(R(0.3, 1.7, 23, 29), T.B).poly(R(0.3, 0.9, 29, 32), T.B).poly(R(1.0, 1.7, 23, 27), T.W);
      p.pal[T.W] = hex("#3f8a4a");
      const [sx, sy] = at(1.3, 38);
      p.ellipse(sx - 3, sy - 2, sx + 2, sy + 2, T.C);
      p.poly(R(0.15, 1.85, 45, 46), T.HI);
      return [p];
    }
    case "miniframe": {
      const p = L(ramp(f.color, ["#ff9ad5", "#7ad8ff", "#ffffff"]));
      p.poly(R(0.2, 0.8, 26, 40), T.BASE).poly(R(0.28, 0.72, 28, 38), T.A).poly(R(0.28, 0.72, 28, 32), T.B);
      stampAt(p, at(0.5, 34), [".x.x.", "xxxxx", ".xxx.", "..x.."], T.C);
      return [p];
    }
    case "skatewall": {
      // three decks hung bottom-out, each with its own graphic
      const d = L(ramp("#2a2a32", [f.color, "#c6ff00", "#7ad8ff", "#f4f4f6"]));
      const cols = [T.A, T.B, T.C];
      for (let i = 0; i < 3; i++) {
        const a0 = 0.2 + i * 0.58, a1 = a0 + 0.42;
        d.poly(R(a0, a1, 16, 48), T.BASE).poly(R(a0 + 0.06, a1 - 0.06, 14, 50), T.BASE);
        d.poly(R(a0 + 0.06, a1 - 0.06, 19, 45), cols[i]);
        d.poly(R(a0 + 0.06, a1 - 0.06, 30, 33), T.W);
        // trucks
        d.poly(R(a0 + 0.1, a1 - 0.1, 20, 21.5), T.HI).poly(R(a0 + 0.1, a1 - 0.1, 42.5, 44), T.HI);
      }
      return [d];
    }
    case "sneakershelf": {
      // two floating shelves, three pairs of kicks on each
      const sh = L(ramp(f.color));
      sh.poly(R(0.1, 2.9, 20, 22.5), T.BASE).poly(R(0.1, 2.9, 36, 38.5), T.BASE);
      sh.poly(R(0.1, 2.9, 20, 21), T.SH).poly(R(0.1, 2.9, 36, 37), T.SH);
      const kicks = L(ramp("#f4f4f8", ["#ff3d8b", "#c6ff00", "#1e1e24", "#d0283a"]));
      kicks.pal[10] = hex("#7ad8ff");
      kicks.pal[11] = hex("#ff7a1a");
      const pair = ["..ccc...ccc.", ".cccc..cccc.", "ccccc.ccccc.", "wwwww.wwwww."];
      const tones = [T.A, T.B, T.C, T.W, 10, 11];
      for (let i = 0; i < 6; i++) {
        const z = i < 3 ? 26 : 42, a = 0.2 + (i % 3) * 0.92;
        wallStamp(kicks, left, s0, a, z, pair, { c: tones[i], w: T.BASE });
      }
      kicks.pal[T.W] = hex("#ff5fa2");
      return [sh, kicks];
    }
    case "graffiti": {
      // a spray-painted tag over a pink splat, with drips
      const splat = L(ramp("#ff5fa2", ["#ff8fc0"]), null);
      splat.poly(R(0.1, 3.9, 16, 44), T.BASE);
      for (const [a, z0] of [[0.3, 13], [1.2, 10], [2.4, 12], [3.4, 9]] as const) splat.poly(R(a, a + 0.12, z0, 16), T.BASE);
      for (let i = 0; i < splat.data.length; i++) if (splat.data[i] && ((i * 2654435761) >>> 28) < 2) splat.data[i] = T.A;
      const tag = L(ramp(f.color, ["#ffffff", "#1e1e24"]), hex("#1e1e24"));
      const rows = word("XROOMS");
      wallStamp(tag, left, s0, 0.55, 38, rows, { x: T.BASE });
      wallStamp(tag, left, s0, 0.55, 38, rows.map((r, j) => (j === 0 ? r : r.replace(/x/g, "."))), { x: T.A });
      for (const a of [0.7, 1.45, 2.2, 3.1]) tag.poly(R(a, a + 0.08, 25, 30), T.BASE);
      return [splat, tag];
    }
    case "stringlights": {
      // fairy lights strung in two loose swags, bulbs hanging off the wire
      const wire = L(ramp("#2a2a32"), null);
      const bulbs = L(ramp(f.color, ["#ff5fa2", "#7ad8ff", "#c6ff00"]));
      const sag = (a: number) => 46 - 6 * Math.sin(Math.PI * ((a - 0.1) / 1.4 % 1));
      for (let a = 0.1; a <= 2.9; a += 1 / 48) wire.set(...at(a, sag(a)), T.BASE);
      const tones = [T.BASE, T.A, T.B, T.C];
      for (let i = 0; i < 10; i++) {
        const a = 0.22 + i * 0.28;
        const [bx, by] = at(a, sag(a));
        bulbs.rect(bx, by + 2, 2, 2, tones[i % 4]);
        wire.set(bx, by + 1, T.BASE);
      }
      return [wire, bulbs];
    }
    case "butterflies": {
      // three chrome and pink butterflies taking off up the wall
      const b = L(ramp(f.color, ["#ff5fa2", "#c9a0ff", "#1e1e24"]));
      const big = ["xxx...xxx", "xyxx.xxyx", "xxxxbxxxx", ".xxxbxxx.", "..xxbxx..", ".xyxbxyx.", ".xx...xx."];
      const small = ["xx.xx", "xxbxx", ".xbx.", "xxbxx", "x...x"];
      wallStamp(b, left, s0, 0.25, 40, big, { x: T.BASE, y: T.A, b: T.C });
      wallStamp(b, left, s0, 1.15, 46, small, { x: T.A, b: T.C });
      wallStamp(b, left, s0, 1.4, 31, small, { x: T.B, b: T.C });
      return [b];
    }
    case "vines": {
      // ivy trailing down from the top of the wall, little flowers along each strand
      const g = L(ramp(f.color), null);
      const fl = L(ramp("#f4f4f6", ["#ff8fc0", "#ffd23f", "#c9a0ff"]));
      const strands: [number, number][] = [[0.15, 18], [0.55, 30], [0.95, 12], [1.35, 24], [1.75, 34], [2.15, 16], [2.55, 26], [2.85, 14]];
      strands.forEach(([a0, len], s) => {
        for (let k = 0; k <= len; k++) {
          const a = a0 + Math.sin(k / 4 + s) / 16, z = 54 - k;
          const [px, py] = at(a, z);
          g.set(px, py, k % 7 < 2 ? T.SH : T.BASE);
          if (k % 4 === 1) g.set(px + (k % 8 < 4 ? -1 : 1), py, T.HI).set(px + (k % 8 < 4 ? -2 : 2), py + 1, T.BASE);
          if (k % 9 === 5 || k === len) {
            const v = [T.A, T.W, T.B, T.C][(s + k) % 4];
            fl.set(px, py - 1, v).set(px - 1, py, v).set(px + 1, py, v).set(px, py + 1, v).set(px, py, T.B);
          }
        }
      });
      // the leafy top edge the strands hang from
      for (let a = 0.05; a <= 2.95; a += 1 / 32) { const [px, py] = at(a, 54); g.set(px, py, T.BASE).set(px, py - 1, (a * 32) % 3 < 1 ? T.HI : T.SH); }
      return [g, fl];
    }
    case "neonrose": {
      // a pink neon rose on a long green stem, bent tube, no board
      const n = L(ramp(f.color, ["#3cff8a", "#ffffff"]));
      const ROSE = [
        "...xxxxx...", "..xx.w.xx..", ".x..xxx..x.", ".x.x.w.x.x.", ".x..x.xx.x.", "..xx...xx..", "....xxx....",
        ".....g.....", "..ggg.g....", ".g...gg....", ".....g.ggg.", ".....gg...g", ".....g.....", ".....g.....", ".....g.....",
      ];
      wallStamp(n, left, s0, 0.16, 48, ROSE, { x: T.BASE, w: T.B, g: T.A });
      return [n];
    }
    case "glowstars": {
      // glow-in-the-dark stars and a moon stuck all over the wall
      const g = L(ramp(f.color, ["#f4ffd0"]));
      const MOON = ["..xxx.", ".xx...", "xx....", "xx....", "xx....", ".xx...", "..xxx."];
      const BIG = ["..x..", ".xxx.", "xxxxx", ".x.x."];
      const SMALL = [".x.", "xxx", ".x."];
      stampAt(g, at(0.35, 46), MOON, T.BASE);
      for (const [a, z, big] of [[0.95, 44, 1], [1.4, 50, 0], [1.8, 38, 1], [2.35, 47, 0], [2.6, 33, 1], [0.6, 32, 0], [1.25, 28, 0], [2.05, 25, 0], [2.8, 20, 0]] as const)
        stampAt(g, at(a, z), big ? BIG : SMALL, T.BASE);
      for (const [a, z] of [[1.1, 40], [2.15, 44], [1.55, 24], [0.5, 22]] as const) g.set(...at(a, z), T.A);
      return [g];
    }
    case "smileyposter": {
      // a yellow smiley on black between two hot pink bands
      const p = L(ramp("#1e1e24", [f.color, "#ff3d8b"]));
      p.poly(R(0.2, 1.8, 18, 48), T.BASE);
      p.poly(R(0.2, 1.8, 18, 21), T.B).poly(R(0.2, 1.8, 45, 48), T.B);
      const FACE = [
        "...xxxxx...", ".xxxxxxxxx.", ".xxxxxxxxx.", "xxxkxxxkxxx", "xxxkxxxkxxx", "xxxxxxxxxxx",
        "xkxxxxxxxkx", "xxkxxxxxkxx", ".xxkkkkkxx.", ".xxxxxxxxx.", "...xxxxx...",
      ];
      stampAt(p, at(1, 32), FACE, T.A);
      return [p];
    }
    case "beadcurtain": {
      // strings of plastic beads hanging in a doorway
      const fr = L(ramp(f.color));
      fr.poly(R(0.06, 0.94, 0, 47), T.BASE).poly(R(0.14, 0.86, 0, 44), T.INK);
      const b = L(ramp("#ff5fa2", ["#ffd23f", "#21d4fd", "#c6ff00", "#8a5ad8"]), null);
      const tones = [T.BASE, T.A, T.B, T.C, T.W];
      b.pal[T.W] = hex("#8a5ad8");
      for (let s = 0; s < 11; s++) {
        const a = 0.18 + s * 0.065;
        for (let z = 43; z > 3; z -= 2) b.set(...at(a, z), tones[(s + Math.floor((43 - z) / 4)) % 5]);
      }
      return [fr, b];
    }
    case "cdrack": {
      // a tall wall rack stacked with CDs in their cases
      const r = L(ramp(f.color, ["#f4f4f6", "#ff5fa2", "#21d4fd", "#ffd23f"]));
      r.poly(R(0.25, 0.75, 8, 52), T.BASE).poly(R(0.32, 0.68, 10, 50), T.INK);
      const tones = [T.A, T.B, T.A, T.C, T.W, T.A, T.B];
      for (let i = 0; i < 19; i++) { if (i % 7 === 5) continue; const z = 11 + i * 2.05; r.poly(R(0.33, 0.67, z, z + 1.2), tones[i % 7]); }
      r.poly(R(0.25, 0.75, 29.5, 31), T.BASE);
      return [r];
    }
    case "vinyl": {
      const v = L(ramp("#1a1a22", [f.color, "#e8e8f0"]));
      const [cx, cy] = at(0.5, 32);
      v.ellipse(cx - 8, cy - 8, cx + 7, cy + 7, T.BASE);
      v.ellipse(cx - 6, cy - 6, cx + 5, cy + 5, T.SH).ellipse(cx - 5, cy - 5, cx + 4, cy + 4, T.BASE);
      v.ellipse(cx - 3, cy - 3, cx + 2, cy + 2, T.A);
      v.set(cx - 1, cy - 1, T.B).set(cx, cy - 1, T.B).set(cx - 1, cy, T.B).set(cx, cy, T.B);
      v.set(cx - 5, cy - 3, T.HI).set(cx - 4, cy - 4, T.HI);
      return [v];
    }
  }
  return null;
}

// ---------------------------------------------------------------- furniture

export interface FurniArt { bmp: Bitmap; x: number; y: number } // x,y: art px offset from (OX, OY)

/**
 * A floor piece's own axes, turned the way it faces: u runs along its width W, v from its back (0) to its
 * front (D). Facing 0 and 1 show the front (+y and +x faces), 2 and 3 show the back.
 */
function frame(f: Furni) {
  const d = f.dir ?? 0;
  const W = d & 1 ? f.h : f.w, D = d & 1 ? f.w : f.h;
  const g = (u: number, v: number): Pt =>
    d === 0 ? [f.x + u, f.y + v] : d === 1 ? [f.x + v, f.y + W - u] : d === 2 ? [f.x + W - u, f.y + D - v] : [f.x + D - v, f.y + u];
  const at = (u: number, v: number, z = 0) => ip(...g(u, v), z);
  /** An upright box over u0..u0+du by v0..v0+dv. */
  const lbox = (Ly: Layer, u0: number, v0: number, du: number, dv: number, z0: number, H: number, top?: number) => {
    const [ax, ay] = g(u0, v0), [bx, by] = g(u0 + du, v0 + dv);
    return box(Ly, Math.min(ax, bx), Math.min(ay, by), Math.abs(bx - ax), Math.abs(by - ay), z0, H, top);
  };
  /** A flat patch on the upright plane at depth v, u0..u1 wide, z0..z1 high. */
  const face = (Ly: Layer, v: number, u0: number, u1: number, z0: number, z1: number, t: number) =>
    Ly.poly([at(u0, v, z0), at(u1, v, z0), at(u1, v, z1), at(u0, v, z1)], t);
  /** A flat patch lying at height z, u0..u1 by v0..v1. */
  const top = (Ly: Layer, u0: number, v0: number, u1: number, v1: number, z: number, t: number) =>
    Ly.poly([at(u0, v0, z), at(u1, v0, z), at(u1, v1, z), at(u0, v1, z)], t);
  /** A flat patch on the upright plane at u (a side), v0..v1 deep, z0..z1 high. */
  const side = (Ly: Layer, u: number, v0: number, v1: number, z0: number, z1: number, t: number) =>
    Ly.poly([at(u, v0, z0), at(u, v1, z0), at(u, v1, z1), at(u, v0, z1)], t);
  /** Which side of the piece faces the viewer: its right (u = W) at turns 0 and 3, its left at 1 and 2. */
  const rightSide = d === 0 || d === 3;
  /** 0..n-1 in back-to-front draw order, for parts lined up along u. */
  const seq = (n: number) => { const i = [...Array(n).keys()]; return d === 0 || d === 3 ? i : i.reverse(); };
  /** Screen depth of a spot on the piece, for ordering parts (bigger is nearer). */
  const near = (u: number, v: number) => { const [a, b] = g(u, v); return a + b; };
  return { d, W, D, back: d >= 2, g, at, lbox, face, top, side, rightSide, seq, near };
}

type BedStyle = "blackout" | "checker" | "chrome" | "canopy";
const BEDS: Record<BedStyle, { plat: string; head: string; pillows: [string, string]; throw?: string }> = {
  blackout: { plat: "#1e1e24", head: "#3a3a46", pillows: ["#c6ff00", "#8c8c98"], throw: "#c6ff00" },
  checker: { plat: "#e8e8ee", head: "#1e1e24", pillows: ["#f4f4f6", "#d0283a"] },
  chrome: { plat: "#c4c8d4", head: "#f4f4f6", pillows: ["#f4f4f6", "#c9a0ff"], throw: "#f4f4f6" },
  canopy: { plat: "#c4c8d4", head: "#f4f4f6", pillows: ["#f4f4f6", "#ff8fc0"] },
};

/**
 * A double bed, headboard at its back: low platform, sheet, duvet turned down at the pillows. Blackout has
 * a lime LED strip under it and a lime throw; checker a checkered duvet; chrome a white faux-fur throw.
 */
function bed(f: Furni, style: BedStyle): Layer[] {
  const F = frame(f);
  const { W, D } = F;
  const st = BEDS[style];
  const plat = L(ramp(st.plat, ["#c6ff00"]));
  F.lbox(plat, 0.02, 0.12, W - 0.04, D - 0.14, 0, 5);
  if (style === "blackout" && !F.back) F.face(plat, D - 0.02, 0.1, W - 0.1, 1, 2, T.A);
  const head = L(ramp(st.head));
  F.lbox(head, 0, 0, W, 0.14, 0, 24);
  F.lbox(head, -0.02, -0.02, W + 0.04, 0.18, 24, 1.5, T.HI);
  // padded channels on the side facing the mattress, seams on the back
  for (let i = 1; i < 4; i++) F.face(head, F.back ? 0 : 0.14, (i * W) / 4 - 0.02, (i * W) / 4 + 0.02, 7, 22, F.back ? T.DEEP : T.SH);
  const sheet = L(ramp("#f4f4f6"));
  F.lbox(sheet, 0.08, 0.16, W - 0.16, D - 0.22, 5, 4);
  const dv = L(ramp(f.color, ["#1e1e24"]));
  F.lbox(dv, 0.04, 1.05, W - 0.08, D - 1.09, 4, 6);
  const S = F.rightSide ? W - 0.04 : 0.04; // the duvet's side we can see
  if (style === "checker") {
    const cu = (W - 0.08) / 4, cv = (D - 1.09) / 4;
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) if ((i + j) % 2) F.top(dv, 0.04 + i * cu, 1.05 + j * cv, 0.04 + (i + 1) * cu, 1.05 + (j + 1) * cv, 10, T.A);
    for (let i = 0; i < 4; i++) for (const [k, z] of [[0, 4], [1, 7]] as const) {
      const odd = (i + k + 1) % 2;
      if (odd && !F.back) F.face(dv, D - 0.04, 0.04 + i * cu, 0.04 + (i + 1) * cu, z, z + 3, T.A);
      // the visible side's checks continue from the top row next to it
      const jj = 3 - i, side = (F.rightSide ? 3 : 0) + jj + k;
      if (side % 2) F.side(dv, S, 1.05 + jj * cv, 1.05 + (jj + 1) * cv, z, z + 3, T.A);
    }
  }
  const fold = L(ramp("#f4f4f6"));
  F.lbox(fold, 0.06, 0.95, W - 0.08 - 0.02, 0.3, 4, 6.5);
  const pillows = F.seq(2).map((i) => {
    const l = L(ramp(st.pillows[i]));
    F.lbox(l, i ? W - 0.9 : 0.14, 0.24, 0.76, 0.56, 9, 3.5);
    return l;
  });
  const parts = [plat, sheet, ...(F.back ? [dv, fold, ...pillows] : [...pillows, dv, fold])];
  if (st.throw) {
    // a throw folded over the foot, hanging down the front; the white one is faux fur
    const th = L(ramp(st.throw));
    F.lbox(th, 0.02, D - 0.62, W - 0.04, 0.46, 4, 6.8);
    if (style === "chrome") for (let i = 0; i < th.data.length; i++) if (th.data[i] && ((i * 2654435761) >>> 28) < 3) th.data[i] = T.SH;
    parts.push(th);
  }
  return F.back ? [...parts, head] : [head, ...parts];
}

type Frame = ReturnType<typeof frame>;
/** A line of pixels across the top of a piece at height z, in its own u/v axes. */
function topLine(Ly: Layer, F: Frame, u0: number, v0: number, u1: number, v1: number, z: number, t: number) {
  const [x0, y0] = F.at(u0, v0, z), [x1, y1] = F.at(u1, v1, z);
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let i = 0; i <= n; i++) { const x = Math.round(x0 + ((x1 - x0) * i) / n), y = Math.round(y0 + ((y1 - y0) * i) / n); if (Ly.get(x, y)) Ly.set(x, y, t); }
}
/** Waterbed comforter styles, picked by the piece's color (its comforter color): colors, headboard books, and the print drawn over the comforter (top z = 16.5). */
const BEDDING: Record<string, { base: string; acc: string[]; fold: string; pillows: [string, string]; books: [string, string, string, string]; draw: (dv: Layer, F: Frame, W: number, D: number) => void }> = {
  memphis: {
    base: "#2fb8a8", acc: ["#ff5fa2", "#ffd23f", "#1e1e24", "#8a5ad8"], fold: "#f4f4f6", pillows: ["#ff8fc0", "#ffd23f"], books: ["#ff5fa2", "#2fb8a8", "#ffd23f", "#8a5ad8"],
    draw: (dv) => {
      for (let y = 0; y < RH; y++) for (let xx = 0; xx < RW; xx++) {
        const v = dv.get(xx, y);
        if (!v || v === T.INK) continue;
        const k = (xx * 7 + y * 13) % 29;
        if (k === 0) dv.set(xx, y, T.A).set(xx + 1, y - 1, T.A).set(xx + 2, y, T.A).set(xx + 3, y - 1, T.A);
        else if (k === 11) dv.set(xx, y, T.B).set(xx + 1, y, T.B);
        else if (k === 19) dv.set(xx, y, T.C);
        else if (k === 24) dv.set(xx, y, T.W);
      }
      dv.pal[T.W] = hex("#8a5ad8");
    },
  },
  plaid: {
    // red flannel: dark checks both ways
    base: "#b8283a", acc: ["#3a1018", "#f4e8d0"], fold: "#f4e8d0", pillows: ["#f4e8d0", "#26262e"], books: ["#b8283a", "#f4e8d0", "#e8b84a", "#5a5a68"],
    draw: (dv, F, W, D) => {
      for (let u = 0.3; u < W - 0.2; u += 0.36) for (const o of [0, 0.03]) topLine(dv, F, u + o, 1.15, u + o, D - 0.18, 16.5, T.A);
      for (let v = 1.3; v < D - 0.2; v += 0.36) for (const o of [0, 0.03]) topLine(dv, F, 0.16, v + o, W - 0.16, v + o, 16.5, T.A);
      for (let u = 0.48; u < W - 0.2; u += 0.36) topLine(dv, F, u, 1.15, u, D - 0.18, 16.5, T.B);
    },
  },
  stars: {
    // navy with a scatter of little yellow stars
    base: "#24306a", acc: ["#ffd23f", "#c6ff7a"], fold: "#f4f4f6", pillows: ["#ffd23f", "#f4f4f6"], books: ["#ffd23f", "#f4f4f6", "#c6ff7a", "#4a5ab8"],
    draw: (dv, F, W, D) => {
      for (let i = 0; i < 14; i++) {
        const u = 0.3 + ((i * 0.53) % (W - 0.6)), v = 1.3 + ((i * 0.37) % (D - 1.6));
        const [px, py] = F.at(u, v, 16.5).map(Math.round);
        if (dv.get(px, py) !== T.BASE) continue;
        const t = i % 3 ? T.A : T.B;
        dv.set(px, py, t).set(px - 1, py, t).set(px + 1, py, t).set(px, py - 1, t).set(px, py + 1, t);
      }
    },
  },
};

export function buildFurni(f: Furni): FurniArt | null {
  const onWall = f.wall !== undefined ? wallPiece(f) : null;
  const layers: Layer[] = onWall ?? [];
  const { x, y, w, h } = f;
  const c = (x0: number, y0: number, z = 0) => ip(x0, y0, z);
  if (!onWall) switch (f.kind) {
    case "mattress": {
      // a bare mattress on the floor with a pillow at one end
      const F = frame(f);
      const m = L(ramp(f.color, ["#ffffff", "#c8ccd8"]));
      box(m, x + 0.06, y + 0.06, w - 0.12, h - 0.12, 0, 6);
      // quilting lines on top
      const n = F.W * 2;
      for (let i = 1; i < n; i++) {
        const u = 0.06 + (i / n) * (F.W - 0.12);
        const [[x0, y0], [x1, y1]] = [F.at(u, 0.1, 6), F.at(u, F.D - 0.1, 6)];
        const k = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
        for (let j = 0; j <= k; j += 2) m.set(Math.round(x0 + ((x1 - x0) * j) / k), Math.round(y0 + ((y1 - y0) * j) / k), T.B);
      }
      layers.push(m);
      const pillow = L(ramp("#ffffff", ["#e8e8f0"]));
      F.lbox(pillow, 0.12, 0.15, 0.5, F.D - 0.3, 6, 3);
      layers.push(pillow);
      break;
    }
    case "rug": {
      const r = L(ramp(f.color, ["#f6e7c8"]));
      r.poly(diamond(f.x + 0.05, f.y + 0.05, f.w - 0.1, f.h - 0.1), T.BASE);
      r.poly(diamond(f.x + 0.3, f.y + 0.3, f.w - 0.6, f.h - 0.6), T.A);
      r.poly(diamond(f.x + 0.4, f.y + 0.4, f.w - 0.8, f.h - 0.8), T.SH);
      r.poly(diamond(f.x + 0.6, f.y + 0.6, f.w - 1.2, f.h - 1.2), T.BASE);
      layers.push(r);
      break;
    }
    case "neon": {
      const n = L(ramp(f.color, [mix(hex(f.color), [255, 255, 255], 0.6)]), null);
      n.poly(diamond(f.x, f.y, f.w, f.h), T.A);
      const hole = new Layer(RW, RH, n.pal);
      hole.poly(diamond(f.x + 0.12, f.y + 0.12, f.w - 0.24, f.h - 0.24), T.BASE);
      for (let i = 0; i < n.data.length; i++) if (hole.data[i]) n.data[i] = 0;
      layers.push(n);
      break;
    }
    case "sofa": {
      const F = frame(f);
      const s = L(ramp(f.color));
      box(s, x + 0.05, y + 0.05, w - 0.1, h - 0.1, 0, 9);
      // seen from the front, the backrest runs between the arms so the far arm reads as one solid side
      // (a full-width backrest overhung it and stuck out like a separate block); from behind it spans the
      // whole width
      const back = L(ramp(f.color));
      if (F.back) F.lbox(back, 0.05, 0.05, F.W - 0.1, 0.35, 9, 10);
      else F.lbox(back, 0.3, 0.05, F.W - 0.6, 0.35, 9, 10);
      // cushions
      const cu = L(ramp(mix(hex(f.color), [255, 255, 255], 0.12)));
      for (const i of F.seq(F.W)) F.lbox(cu, i + 0.1, 0.45, 0.8, F.D - 0.55, 9, 3);
      // arms
      const arm = L(ramp(f.color));
      for (const i of F.seq(2)) F.lbox(arm, i ? F.W - 0.35 : 0.05, 0.05, 0.3, F.D - 0.1, 9, 6);
      // turned away, the backrest is the nearest part
      layers.push(s, ...(F.back ? [cu, arm, back] : [back, cu, arm]));
      break;
    }
    case "table": {
      const legs = L(ramp(mix(hex(f.color), [0, 0, 0], 0.3)));
      for (const [lx, ly] of [[x + 0.15, y + 0.15], [x + w - 0.3, y + 0.15], [x + 0.15, y + h - 0.3], [x + w - 0.3, y + h - 0.3]]) box(legs, lx, ly, 0.15, 0.15, 0, 9);
      layers.push(legs);
      const top = L(ramp(f.color));
      box(top, x + 0.05, y + 0.05, w - 0.1, h - 0.1, 9, 3);
      layers.push(top);
      // magazine + mug
      const F = frame(f);
      const deco = L(ramp("#ff7fbf", ["#ffffff"]));
      F.lbox(deco, 0.3, 0.25, 0.45, 0.35, 12, 1);
      F.lbox(deco, F.W - 0.6, 0.35, 0.2, 0.2, 12, 4, T.A);
      layers.push(deco);
      break;
    }
    case "crate": {
      const F = frame(f);
      const b = L(ramp(f.color, ["#ead9b4", "#1e1e24"]));
      box(b, x + 0.05, y + 0.05, w - 0.1, h - 0.1, 0, 15);
      for (const z of [5, 10]) {
        b.poly([c(x + 0.05, y + h - 0.05, z), c(x + w - 0.05, y + h - 0.05, z), c(x + w - 0.05, y + h - 0.05, z + 1), c(x + 0.05, y + h - 0.05, z + 1)], T.DEEP);
      }
      // shipping label on its front, so you can tell which way it's turned
      if (!F.back) {
        const m = F.W / 2;
        F.face(b, F.D - 0.05, m - 0.28, m + 0.28, 6.5, 9.5, T.A);
        F.face(b, F.D - 0.05, m - 0.2, m + 0.08, 7.8, 8.4, T.B);
      }
      layers.push(b);
      break;
    }
    case "cabinet": {
      const F = frame(f);
      const b = L(ramp("#2a2a38", [f.color, mix(hex(f.color), [255, 255, 255], 0.5)]));
      const deck = () => F.lbox(b, 0.1, 0.75, 0.8, 0.25, 12, 3);
      if (F.back) deck(); // behind the cabinet
      F.lbox(b, 0.1, 0.15, 0.8, 0.7, 0, 34);
      if (!F.back) {
        // screen and marquee on the front, then the control deck
        F.face(b, 0.85, 0.2, 0.8, 18, 28, T.A);
        F.face(b, 0.85, 0.25, 0.5, 20, 26, T.B);
        F.face(b, 0.85, 0.1, 0.9, 30, 33, T.A);
        deck();
        b.set(...F.at(0.35, 0.88, 15), T.B).set(...F.at(0.65, 0.88, 15), T.A);
      } else {
        // the back: service panel with vents, the marquee's lit edge
        F.face(b, 0.15, 0.22, 0.78, 6, 26, T.SH);
        for (const z of [19, 21.5, 24]) F.face(b, 0.15, 0.3, 0.7, z - 0.6, z, T.DEEP);
        F.face(b, 0.15, 0.3, 0.46, 9, 13, T.A);
        F.face(b, 0.15, 0.1, 0.9, 30, 33, T.A);
      }
      layers.push(b);
      break;
    }
    case "plant": {
      const pot = L(ramp("#d8794a", ["#f0a070"]));
      box(pot, x + 0.28, y + 0.28, 0.44, 0.44, 0, 8);
      box(pot, x + 0.24, y + 0.24, 0.52, 0.52, 8, 2, T.A);
      layers.push(pot);
      const leaves = L(ramp(f.color));
      const [px, py] = c(x + 0.5, y + 0.5, 10);
      const k = (f.dir ?? 0) & 1 ? -1 : 1; // a quarter turn shows its other side
      for (const [dx, dy, rx, ry] of [[-6, -4, 6, 5], [5, -4, 6, 5], [0, -10, 7, 6], [-4, -16, 5, 5], [4, -17, 5, 5], [0, -21, 4, 4]]) leaves.ellipse(px + k * dx - rx, py + dy - ry, px + k * dx + rx, py + dy + ry, T.BASE);
      leaves.shade({ right: 2, bottom: 1 });
      for (const [dx, dy] of [[-6, -6], [3, -12], [-3, -18], [5, -6]]) leaves.set(px + k * dx, py + dy, T.HI).set(px + k * dx + 1, py + dy, T.HI);
      layers.push(leaves);
      break;
    }
    case "sunflowers": {
      // three tall sunflowers in a matte black pot with a lime band
      const pot = L(ramp("#26262e", ["#c6ff00"]));
      box(pot, x + 0.3, y + 0.3, 0.4, 0.4, 0, 9);
      box(pot, x + 0.27, y + 0.27, 0.46, 0.46, 9, 2, T.HI);
      const [px, py] = c(x + 0.5, y + 0.5, 11);
      for (let i = -6; i <= 6; i++) pot.set(px + i, py + 6 - Math.round(Math.abs(i) / 2), T.A);
      layers.push(pot);
      const k = (f.dir ?? 0) & 1 ? -1 : 1;
      const stems = L(ramp("#3f8a4a"));
      const heads: [number, number, number][] = [[4, 40, 6], [-5, 32, 5], [1, 22, 4]];
      for (const [dx, h] of heads) for (let j = 1; j <= h; j++) stems.set(px + k * Math.round(dx * (j / h)), py - j, T.BASE);
      for (const [dx, dy] of [[-4, -8], [4, -14], [-3, -20]]) stems.ellipse(px + k * dx - 3, py + dy - 1, px + k * dx + 2, py + dy + 1, T.BASE);
      stems.shade({ right: 1, bottom: 1 });
      layers.push(stems);
      for (const [dx, h, r] of heads) {
        const l = L(ramp(f.color, ["#6a3c18", "#3a2010"]));
        const hx = px + k * dx, hy = py - h;
        l.ellipse(hx - r, hy - r + 1, hx + r, hy + r - 1, T.BASE);
        for (const [ex, ey] of [[0, -r], [0, r], [-r - 1, 0], [r + 1, 0]]) l.set(hx + ex, hy + ey, T.BASE);
        l.shade({ right: 1, bottom: 1 });
        const q = r >> 1;
        l.ellipse(hx - q - 1, hy - q, hx + q, hy + q - 1, T.A);
        l.set(hx - 1, hy - 1, T.B).set(hx, hy, T.B);
        layers.push(l);
      }
      break;
    }
    case "tulips": {
      // a bunch of tulips in a chrome pot, long blade leaves
      const pot = L(ramp("#c4c8d4"));
      box(pot, x + 0.32, y + 0.32, 0.36, 0.36, 0, 10, T.HI);
      layers.push(pot);
      const [px, py] = c(x + 0.5, y + 0.5, 10);
      const k = (f.dir ?? 0) & 1 ? -1 : 1;
      const g = L(ramp("#4caf50"));
      const blooms: [number, number][] = [[-5, 20], [-2, 26], [2, 23], [5, 18], [0, 16]];
      for (const [dx, h] of blooms) for (let j = 0; j <= h; j++) g.set(px + k * Math.round(dx * (j / h)), py - j, T.BASE);
      for (const s of [-1, 1]) for (let j = 0; j < 9; j++) g.set(px + k * s * (2 + Math.round(j / 2)), py - 1 - j, T.BASE).set(px + k * s * (3 + Math.round(j / 2)), py - 1 - j, j > 6 ? T.HI : T.SH);
      layers.push(g);
      const b = L(ramp(f.color, ["#ffffff"]));
      for (const [dx, h] of blooms) b.stamp(px + k * dx - 2, py - h - 4, ["x.x.x", "xxxxx", "xxxxx", ".xxx."], { x: T.BASE });
      b.shade({ right: 1, bottom: 1 });
      for (const [dx, h] of blooms) b.set(px + k * dx - 1, py - h - 2, T.HI);
      layers.push(b);
      break;
    }
    case "roses": {
      // a dozen roses in a tall glass vase
      const [px, py] = c(x + 0.5, y + 0.5);
      const vase = L(ramp("#bfe0f0", ["#ffffff", "#7aa8c0"]));
      vase.spans([[py - 18, px - 3, px + 2], [py - 17, px - 3, px + 2], [py - 16, px - 3, px + 2], [py - 15, px - 4, px + 3], [py - 14, px - 4, px + 3], [py - 13, px - 4, px + 3], [py - 12, px - 4, px + 3], [py - 11, px - 4, px + 3], [py - 10, px - 4, px + 3], [py - 9, px - 4, px + 3], [py - 8, px - 4, px + 3], [py - 7, px - 4, px + 3], [py - 6, px - 4, px + 3], [py - 5, px - 4, px + 3], [py - 4, px - 4, px + 3], [py - 3, px - 4, px + 3], [py - 2, px - 3, px + 2]]);
      vase.shade({ right: 2, bottom: 1 });
      for (let j = 6; j < 17; j++) vase.set(px - 3, py - j, T.A); // shine
      for (let i = -3; i <= 2; i++) vase.set(px + i, py - 8, T.C); // water line
      vase.vline(px - 1, py - 14, py - 3, T.C).vline(px + 1, py - 15, py - 4, T.C); // stems through the glass
      layers.push(vase);
      const k = (f.dir ?? 0) & 1 ? -1 : 1;
      const g = L(ramp("#2f6b4a"));
      const heads: [number, number][] = [[-5, 28], [0, 33], [5, 29], [-2, 24], [3, 23], [-7, 21], [7, 20]];
      for (const [dx, h] of heads) for (let j = 18; j <= h; j++) g.set(px + k * Math.round(dx * ((j - 18) / (h - 18))), py - j, T.BASE);
      g.ellipse(px - 5, py - 21, px - 2, py - 19, T.BASE).ellipse(px + 2, py - 22, px + 5, py - 20, T.BASE);
      layers.push(g);
      // each head on its own layer so its outline keeps it apart from the ones behind it
      for (const [dx, h] of [...heads].sort((a, b) => a[1] - b[1]).reverse()) {
        const r = L(ramp(f.color, [mix(hex(f.color), [255, 60, 110], 0.35)]));
        const hx = px + k * dx, hy = py - h;
        r.ellipse(hx - 3, hy - 2, hx + 2, hy + 2, T.BASE);
        r.set(hx - 1, hy - 1, T.A).set(hx, hy - 1, T.A).set(hx, hy, T.SH).set(hx - 1, hy + 1, T.A).set(hx + 1, hy, T.A).set(hx - 2, hy - 1, T.HI);
        layers.push(r);
      }
      break;
    }
    case "planter": {
      // a long concrete planter packed with mixed flowers
      const F = frame(f);
      const box1 = L(ramp(f.color));
      F.lbox(box1, 0.04, 0.22, F.W - 0.08, 0.56, 0, 9);
      F.lbox(box1, 0, 0.18, F.W, 0.64, 9, 2, T.HI);
      if (!F.back) F.face(box1, 0.78, 0.15, F.W - 0.15, 3, 3.6, T.DEEP);
      layers.push(box1);
      const soil = L(ramp("#4a3020"), null);
      F.top(soil, 0.1, 0.26, F.W - 0.1, 0.74, 11, T.BASE);
      layers.push(soil);
      const leaves = L(ramp("#3f8a4a"));
      const fl = L(ramp("#f4f4f6", ["#ff5fa2", "#ffd23f", "#c9a0ff", "#ff7a1a"])); // the last accent takes the W slot
      const spots: [number, number, number][] = [];
      for (let i = 0; i < 9; i++) spots.push([0.18 + (i * (F.W - 0.36)) / 8, i % 2 ? 0.36 : 0.62, 6 + ((i * 5) % 7)]);
      spots.sort((a, b) => F.near(a[0], a[1]) - F.near(b[0], b[1]));
      spots.forEach(([u, v, h], i) => {
        const [sx, sy] = F.at(u, v, 11);
        leaves.ellipse(sx - 2, sy - 3, sx + 2, sy, T.BASE);
        for (let j = 1; j <= h; j++) leaves.set(sx, sy - j, T.SH);
        const t = [T.A, T.BASE, T.B, T.C, T.W][i % 5]; // pink, white, yellow, lilac, orange
        fl.ellipse(sx - 2, sy - h - 2, sx + 1, sy - h + 1, t);
        fl.set(sx - 1, sy - h - 1, t === T.B ? T.W : T.B);
      });
      leaves.shade({ right: 1, bottom: 0 });
      layers.push(leaves, fl);
      break;
    }
    case "blossom": {
      // a little cherry blossom tree in a white pot, petals dropped on the floor
      const pot = L(ramp("#f4f4f6"));
      box(pot, x + 0.3, y + 0.3, 0.4, 0.4, 0, 9);
      box(pot, x + 0.27, y + 0.27, 0.46, 0.46, 9, 2, T.HI);
      const [px, py] = c(x + 0.5, y + 0.5, 11);
      const k = (f.dir ?? 0) & 1 ? -1 : 1;
      const petals = L(ramp(f.color), null);
      const [fx, fy] = c(x + 0.5, y + 0.5);
      for (const [dx, dy] of [[-9, 0], [-7, 3], [8, 1], [10, -2], [5, 4], [-3, 5], [-11, -2], [2, -4]]) petals.set(fx + k * dx, fy + dy, T.BASE).set(fx + k * dx + 1, fy + dy, T.HI);
      layers.push(petals, pot);
      const trunk = L(ramp("#5a3a2a"));
      for (let j = 0; j <= 26; j++) { const bx = px + k * Math.round(Math.sin(j / 7) * 3); trunk.rect(bx - (j < 10 ? 1 : 0), py - j, 2, 1, T.BASE); }
      for (let j = 0; j < 8; j++) trunk.set(px + k * (3 + j), py - 20 - Math.round(j * 0.8), T.BASE).set(px - k * (1 + j), py - 24 - Math.round(j * 0.6), T.BASE);
      trunk.shade({ right: 1, bottom: 0 });
      layers.push(trunk);
      const can = L(ramp(f.color, ["#ff7fae", "#ffffff"]));
      const [cx, cy] = [px, py - 34];
      for (const [dx, dy, rx, ry] of [[0, 0, 13, 8], [-9, 3, 7, 6], [10, 1, 7, 6], [-4, -6, 8, 6], [6, -6, 7, 5], [0, -10, 6, 4]]) can.ellipse(cx + k * dx - rx, cy + dy - ry, cx + k * dx + rx, cy + dy + ry, T.BASE);
      can.shade({ right: 2, bottom: 2 });
      for (const [dx, dy, t] of [[-6, -4, T.A], [3, -9, T.B], [-10, 2, T.B], [8, -2, T.A], [0, 3, T.A], [-2, -8, T.HI], [5, 4, T.B], [11, -4, T.HI], [-13, 5, T.A], [2, -2, T.B], [-7, 6, T.B], [12, 3, T.A], [-3, -13, T.A], [7, -9, T.B]]) can.set(cx + k * dx, cy + dy, t).set(cx + k * dx + 1, cy + dy, t);
      layers.push(can);
      break;
    }
    case "lamp": {
      const [px, py] = c(x + 0.5, y + 0.5);
      const pole = L(ramp("#4a4a58"));
      pole.ellipse(px - 5, py - 2, px + 4, py + 1, T.BASE);
      pole.rect(px - 1, py - 34, 2, 33, T.BASE);
      pole.shade({ right: 1, bottom: 1 });
      layers.push(pole);
      const shadeL = L(ramp(f.color, ["#ffffff"]));
      shadeL.spans([[py - 44, px - 4, px + 3], [py - 43, px - 5, px + 4], [py - 42, px - 5, px + 4], [py - 41, px - 6, px + 5], [py - 40, px - 6, px + 5], [py - 39, px - 7, px + 6], [py - 38, px - 7, px + 6], [py - 37, px - 7, px + 6]]);
      shadeL.shade({ right: 2, bottom: 1 });
      shadeL.hline(px - 3, px - 1, py - 42, T.A);
      layers.push(shadeL);
      break;
    }
    case "tv": {
      const F = frame(f);
      const stand = L(ramp("#3a3a48"));
      box(stand, x + 0.15, y + 0.15, 0.7, 0.7, 0, 10);
      layers.push(stand);
      const tv = L(ramp(f.color, ["#2a3a8a", "#7ad8ff", "#c9f0ff"]));
      F.lbox(tv, 0.2, 0.2, 0.62, 0.62, 10, 16);
      const hump = L(tv.pal);
      if (!F.back) {
        F.face(tv, 0.82, 0.27, 0.72, 13, 24, T.A);
        F.face(tv, 0.82, 0.32, 0.45, 21, 23, T.C);
        for (const z of [15, 18]) F.face(tv, 0.82, 0.3, 0.7, z, z + 0.6, T.B);
      } else {
        // the tube's back hump, with vents
        F.lbox(hump, 0.3, 0.06, 0.42, 0.14, 12, 11);
        for (const z of [16, 18.5]) F.face(hump, 0.06, 0.36, 0.66, z, z + 0.6, T.DEEP);
      }
      // antenna
      const [ax, ay] = c(x + 0.5, y + 0.5, 26);
      for (let i = 1; i < 9; i++) { tv.set(ax - Math.round(i * 0.7), ay - i, T.INK); tv.set(ax + Math.round(i * 0.7), ay - i, T.INK); }
      tv.ellipse(ax - 2, ay - 1, ax + 1, ay + 1, T.SH);
      layers.push(tv, hump);
      break;
    }
    case "beanbag": {
      const b = L(ramp(f.color));
      const [px, py] = c(x + 0.5, y + 0.5);
      // the backrest bulge sits on the side away from where it faces
      const [bx, by] = ([[3, -1], [-4, -1], [-3, 2], [3, 2]] as const)[f.dir ?? 0];
      b.ellipse(px - 12, py - 9, px + 11, py + 3, T.BASE);
      b.ellipse(px - 9 + bx, py - 16 + by, px + 6 + bx, py - 5 + by, T.BASE);
      b.shade({ right: 3, bottom: 2 });
      b.hline(px - 8 + bx, px + 2 + bx, py - 6 + by, T.SH).hline(px - 6 + bx, px - 2 + bx, py - 14 + by, T.HI);
      layers.push(b);
      break;
    }
    case "lavalamp": {
      const [px, py] = c(x + 0.5, y + 0.5);
      const base = L(ramp("#b8bcc8"));
      base.spans([[py - 6, px - 3, px + 2], [py - 5, px - 3, px + 2], [py - 4, px - 4, px + 3], [py - 3, px - 4, px + 3], [py - 2, px - 5, px + 4], [py - 1, px - 5, px + 4]]);
      base.spans([[py - 27, px - 2, px + 1], [py - 26, px - 2, px + 1]]);
      base.shade({ right: 2, bottom: 0 });
      layers.push(base);
      const glass = L(ramp(mix(hex(f.color), [255, 255, 255], 0.55), [f.color]));
      glass.spans([[py - 25, px - 2, px + 1], [py - 24, px - 3, px + 2], [py - 23, px - 3, px + 2], [py - 22, px - 4, px + 3], [py - 21, px - 4, px + 3], [py - 20, px - 4, px + 3], [py - 19, px - 5, px + 4], [py - 18, px - 5, px + 4], [py - 17, px - 5, px + 4], [py - 16, px - 5, px + 4], [py - 15, px - 5, px + 4], [py - 14, px - 4, px + 3], [py - 13, px - 4, px + 3], [py - 12, px - 4, px + 3], [py - 11, px - 4, px + 3], [py - 10, px - 3, px + 2], [py - 9, px - 3, px + 2], [py - 8, px - 3, px + 2], [py - 7, px - 3, px + 2]]);
      glass.ellipse(px - 2, py - 22, px + 1, py - 19, T.A).ellipse(px - 3, py - 13, px + 2, py - 8, T.A).ellipse(px, py - 17, px + 2, py - 15, T.A);
      glass.vline(px - 3, py - 18, py - 12, T.HI);
      layers.push(glass);
      break;
    }
    case "shoeboxes": {
      // three orange shoeboxes stacked a little off-square
      const F = frame(f);
      const b = L(ramp(f.color, ["#ffffff", "#1e1e24"]));
      const stack: [number, number, number][] = [[0.15, 0.25, 0], [0.22, 0.2, 7], [0.12, 0.28, 14]];
      for (const [ou, ov, z] of stack) {
        F.lbox(b, ou, ov, 0.7, 0.5, z, 7);
        // lid line all round; the size label is on the end facing out
        const v = F.back ? ov : ov + 0.5;
        F.face(b, v, ou, ou + 0.7, z + 5, z + 5.8, T.DEEP);
        if (!F.back) F.face(b, v, ou + 0.08, ou + 0.25, z + 1.5, z + 3.5, T.A);
      }
      layers.push(b);
      break;
    }
    case "speaker": {
      // two cabinets stacked, woofers facing the room
      const s = L(ramp(f.color, ["#4a4a58", "#9a9aac", "#c6ff00"]));
      const F = frame(f);
      box(s, x + 0.15, y + 0.15, 0.7, 0.7, 0, 16);
      box(s, x + 0.2, y + 0.2, 0.6, 0.6, 16, 14);
      if (!F.back) {
        for (const [z, r, v] of [[8, 5, 0.85], [23, 4, 0.8]] as const) {
          const [px, py] = F.at(0.5, v, z);
          s.ellipse(px - r, py - r, px + r - 1, py + r - 1, T.A);
          s.ellipse(px - r + 2, py - r + 2, px + r - 3, py + r - 3, T.INK);
          s.set(px - 1, py - 1, T.B).set(px, py - 1, T.B);
        }
        s.set(...F.at(0.75, 0.8, 28), T.C);
      } else {
        // back plates: amp with jacks below, bass port above
        F.face(s, 0.15, 0.25, 0.75, 3, 12, T.A);
        for (const u of [0.38, 0.5, 0.62]) s.set(...F.at(u, 0.15, 6), T.INK);
        s.set(...F.at(0.66, 0.15, 10), T.C);
        const [px, py] = F.at(0.5, 0.2, 23);
        s.ellipse(px - 3, py - 2, px + 2, py + 1, T.INK);
      }
      layers.push(s);
      break;
    }
    case "vending": {
      // soda machine: tall body, glass front full of cans, lit sign
      const v = L(ramp(f.color, ["#1a1a24", "#f4f4f8", "#ff3d8b", "#7ad8ff", "#ffffff"]));
      const F = frame(f);
      F.lbox(v, 0.12, 0.18, 0.76, 0.66, 0, 40);
      if (!F.back) {
        const face = (u0: number, u1: number, z0: number, z1: number, t: number) => F.face(v, 0.84, u0, u1, z0, z1, t);
        face(0.18, 0.62, 10, 36, T.A); // glass
        for (let row = 0; row < 4; row++) for (let col = 0; col < 3; col++) {
          const [px, py] = F.at(0.24 + col * 0.13, 0.84, 14 + row * 6);
          v.rect(px, py - 2, 2, 3, [T.B, T.C, T.W][(row + col) % 3]);
        }
        face(0.67, 0.82, 26, 34, T.SH); // keypad
        face(0.69, 0.8, 17, 20, T.INK); // coin slot
        face(0.2, 0.6, 3, 7, T.INK); // pickup
        face(0.12, 0.88, 37, 40, T.HI); // sign strip
      } else {
        // the back: service panel, cooling vents, the sign strip wrapping round
        F.face(v, 0.18, 0.2, 0.8, 4, 33, T.SH);
        for (const z of [8, 10.5, 13]) F.face(v, 0.18, 0.3, 0.7, z - 0.7, z, T.DEEP);
        F.face(v, 0.18, 0.56, 0.72, 27, 30, T.W);
        F.face(v, 0.18, 0.12, 0.88, 37, 40, T.HI);
      }
      layers.push(v);
      break;
    }
    case "rack": {
      // chrome rail with tees and hoodies hanging off it; turns with the piece
      const F = frame(f);
      const len = F.W;
      const rail = L(ramp(f.color));
      const bar = (u0: number, u1: number, v0: number, z0: number, z1: number) => F.lbox(rail, u0, v0, u1 - u0, 0.08, z0, z1 - z0);
      bar(0.1, 0.18, 0.46, 0, 34); bar(len - 0.18, len - 0.1, 0.46, 0, 34);
      bar(0.1, len - 0.1, 0.46, 32, 34);
      bar(0.05, 0.25, 0.3, 0, 2); bar(len - 0.25, len - 0.05, 0.3, 0, 2);
      layers.push(rail);
      const tones = ["#1e1e24", "#c6ff00", "#f4f4f6", "#a8262e", "#4a2f8a", "#8c8c98"];
      const n = len * 3;
      for (const i of F.seq(n)) {
        const g = L(ramp(tones[i % tones.length]));
        const u = 0.3 + (i * (len - 0.6)) / (n - 1) - 0.1;
        F.lbox(g, u, 0.3, 0.2, 0.4, 14 + (i % 2) * 3, 17 - (i % 2) * 3);
        const [hx, hy] = F.at(u + 0.1, 0.5, 32);
        g.set(hx, hy, T.DEEP).set(hx, hy + 1, T.DEEP);
        layers.push(g);
      }
      break;
    }
    case "djbooth": {
      // booth with an LED strip on the front, two decks and a mixer on top; turns with the piece
      const F = frame(f);
      const b = L(ramp(f.color, ["#c6ff00", "#3a3a48", "#ff3d8b", "#e8e8f0"]));
      F.lbox(b, 0.05, 0.1, F.W - 0.1, F.D - 0.2, 0, 16);
      if (!F.back) F.face(b, F.D - 0.1, 0.15, F.W - 0.15, 9, 11, T.A); // LED strip on the crowd's side
      else {
        // the DJ's side: an open shelf with a laptop and a sticker
        F.face(b, 0.1, 0.2, F.W - 0.2, 3, 12, T.B);
        F.face(b, 0.1, 0.35, 0.95, 6, 7, T.W);
        F.face(b, 0.1, F.W - 0.65, F.W - 0.35, 5, 9, T.C);
      }
      layers.push(b);
      const gear = L(b.pal.slice() as RGB[]);
      for (const k of F.seq(2)) {
        const [gx, gy] = F.g(0.45 + k * (F.W - 0.9), 0.5);
        box(gear, gx - 0.3, gy - 0.3, 0.6, 0.6, 16, 2, T.B);
        const [px, py] = c(gx, gy, 18);
        gear.ellipse(px - 6, py - 3, px + 5, py + 2, T.INK);
        gear.ellipse(px - 2, py - 1, px + 1, py, T.C);
        gear.set(px + 4, py - 3, T.W);
      }
      F.lbox(gear, F.W / 2 - 0.15, 0.25, 0.3, 0.5, 16, 3, T.SH);
      const [kx, ky] = F.at(F.W / 2, 0.5, 19);
      gear.set(kx - 1, ky, T.A).set(kx + 1, ky, T.C);
      layers.push(gear);
      break;
    }
    case "bed":
    case "checkbed":
    case "chromebed":
      layers.push(...bed(f, f.kind === "bed" ? "blackout" : f.kind === "checkbed" ? "checker" : "chrome"));
      break;
    case "canopybed": {
      // the chrome bed under a four-post canopy, sheer lilac drapes tied back at each post and a valance up top
      const F = frame(f);
      const metal = ramp("#c4c8d4");
      const voile = ramp("#d9bfff");
      const mid = F.near(F.W / 2, F.D / 2);
      const post = (u: number, v: number) => {
        const l = L(metal);
        F.lbox(l, u, v, 0.08, 0.08, 0, 54);
        const c = L(voile);
        // drape gathered at the post: wide at the top, pinched at the tie, flared at the floor
        const cu = u < F.W / 2 ? u + 0.06 : u - 0.18, cv = v < F.D / 2 ? v + 0.06 : v - 0.18;
        F.lbox(c, cu, cv, 0.2, 0.2, 30, 20);
        F.lbox(c, cu + 0.05, cv + 0.05, 0.1, 0.1, 26, 4);
        F.lbox(c, cu, cv, 0.2, 0.2, 0, 26);
        const tie = L(ramp("#ff5fa2"));
        F.lbox(tie, cu + 0.04, cv + 0.04, 0.12, 0.12, 26, 2);
        return { d: F.near(u + 0.04, v + 0.04), ls: [l, c, tie] };
      };
      const posts = [post(0, 0), post(F.W - 0.08, 0), post(0, F.D - 0.08), post(F.W - 0.08, F.D - 0.08)];
      const top = L(voile);
      for (const [u0, v0, du, dv] of [[0, 0, F.W, 0.08], [0, F.D - 0.08, F.W, 0.08], [0, 0, 0.08, F.D], [F.W - 0.08, 0, 0.08, F.D]]) F.lbox(top, u0, v0, du, dv, 49, 5);
      const rail = L(metal);
      F.lbox(rail, 0, 0, F.W, F.D, 54, 1.5);
      const hole = new Layer(RW, RH, rail.pal);
      F.lbox(hole, 0.08, 0.08, F.W - 0.16, F.D - 0.16, 54, 1.5);
      for (let i = 0; i < rail.data.length; i++) if (hole.data[i]) rail.data[i] = 0;
      layers.push(...posts.filter((p) => p.d < mid).flatMap((p) => p.ls), ...bed(f, "canopy"), ...posts.filter((p) => p.d >= mid).flatMap((p) => p.ls), top, rail);
      break;
    }
    case "vanity": {
      // white vanity table with a light-up mirror, perfume and a lipstick on top
      const F = frame(f);
      const t = L(ramp(f.color, ["#ff5fa2", "#c4c8d4"]));
      F.lbox(t, 0.06, 0.2, 0.5, 0.66, 0, 14);
      F.lbox(t, F.W - 0.56, 0.2, 0.5, 0.66, 0, 14);
      if (!F.back) for (const u of [0.31, F.W - 0.31]) { F.face(t, 0.86, u - 0.22, u + 0.22, 7, 7.6, T.DEEP); for (const z of [3.5, 10]) F.face(t, 0.86, u - 0.07, u + 0.07, z, z + 1, T.C); }
      else F.face(t, 0.2, 0.56, F.W - 0.56, 9, 13, T.SH);
      F.lbox(t, 0.02, 0.16, F.W - 0.04, 0.74, 14, 2);
      layers.push(t);
      const m = L(ramp("#f4f4f6", ["#bfe0f0", "#ffffff", "#ffe9a8"]));
      F.lbox(m, 0.28, 0.18, F.W - 0.56, 0.1, 16, 26);
      if (!F.back) {
        F.face(m, 0.28, 0.4, F.W - 0.4, 19, 39, T.A);
        for (const [u, z] of [[0.6, 36], [0.7, 33], [1.2, 30]] as const) F.face(m, 0.28, u, u + 0.06, z - 6, z, T.B); // shine
        // bulbs all round the frame
        for (let i = 0; i <= 6; i++) { const u = 0.36 + (i * (F.W - 0.72)) / 6; m.rect(...F.at(u, 0.28, 40.5), 2, 1, T.C); }
        for (let i = 0; i < 4; i++) for (const u of [0.33, F.W - 0.33]) m.rect(...F.at(u, 0.28, 22 + i * 5), 1, 2, T.C);
      } else F.face(m, 0.18, 0.36, F.W - 0.36, 19, 39, T.SH);
      const bits = L(ramp("#ff8fc0", ["#c4c8d4", "#ff3d8b"]));
      F.lbox(bits, F.W - 0.55, 0.5, 0.16, 0.16, 16, 5);
      F.lbox(bits, F.W - 0.51, 0.54, 0.08, 0.08, 21, 1.5, T.A);
      F.lbox(bits, 0.45, 0.6, 0.08, 0.08, 16, 3, T.B);
      layers.push(...(F.back ? [bits, m] : [m, bits]));
      break;
    }
    case "bubblechair": {
      // see-through inflatable armchair: a ring seat, two tube arms and a tube back
      const F = frame(f);
      // light, low-contrast shading so it reads as glossy see-through plastic
      const pal = ramp(mix(hex(f.color), [255, 255, 255], 0.2), [mix(hex(f.color), [255, 255, 255], 0.5)]);
      pal[T.SH] = hex(f.color);
      pal[T.DEEP] = mix(hex(f.color), [60, 20, 80], 0.2);
      const tube = (u0: number, v0: number, du: number, dv: number, z0: number, H: number) => {
        const l = L(pal);
        F.lbox(l, u0, v0, du, dv, z0, H, T.A);
        return l;
      };
      const seat = tube(0.14, 0.2, 0.72, 0.68, 0, 8);
      const back = tube(0.12, 0.12, 0.76, 0.2, 8, 14);
      const arms = F.seq(2).map((i) => tube(i ? 0.72 : 0.1, 0.3, 0.18, 0.56, 8, 7));
      for (const l of [seat, back, ...arms]) {
        // a glossy streak just inside the top-left edge of each tube, so it reads as plastic
        let x0 = 0;
        while (x0 < RW && ![...Array(RH).keys()].some((y) => l.get(x0, y))) x0++;
        const cx = x0 + 2;
        let top = 0;
        while (top < RH && !l.get(cx, top)) top++;
        for (let y = top + 2; y <= top + 4; y++) if (l.get(cx, y) && l.get(cx, y + 2)) l.set(cx, y, T.W);
        if (l.get(cx + 1, top + 2)) l.set(cx + 1, top + 2, T.W);
      }
      layers.push(seat, ...(F.back ? [...arms, back] : [back, ...arms]));
      break;
    }
    case "floormirror": {
      // full-length mirror on a stand, a couple of photos tucked into the frame
      const F = frame(f);
      const m = L(ramp(f.color, ["#bfe0f0", "#ffffff", "#ff5fa2", "#1e1e24"]));
      F.lbox(m, 0.2, 0.55, 0.6, 0.16, 0, 1.5);
      F.lbox(m, 0.24, 0.4, 0.52, 0.1, 1.5, 46);
      if (!F.back) {
        F.face(m, 0.5, 0.29, 0.71, 4, 45, T.A);
        for (const [u, z] of [[0.36, 38], [0.42, 30], [0.56, 22]] as const) F.face(m, 0.5, u, u + 0.05, z - 7, z, T.B);
        F.face(m, 0.5, 0.5, 0.68, 36, 44, T.W); F.face(m, 0.5, 0.53, 0.65, 38, 43, T.C); // photo
        F.face(m, 0.5, 0.3, 0.42, 7, 12, T.W);
      } else {
        F.face(m, 0.4, 0.3, 0.7, 6, 44, T.SH);
        F.lbox(m, 0.46, 0.14, 0.08, 0.28, 0, 2, T.DEEP); // kick stand
      }
      layers.push(m);
      break;
    }
    case "shagrug": {
      // a round shaggy rug, fibres speckled light and dark
      const r = L(ramp(f.color));
      const [cx, cy] = ip(x + w / 2, y + h / 2), rx = w * 16 - 2, ry = h * 8 - 1;
      r.ellipse(cx - rx, cy - ry, cx + rx, cy + ry, T.BASE);
      for (let i = 0; i < r.data.length; i++) if (r.data[i]) { const k = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1; r.data[i] = k < 0.2 ? T.HI : k > 0.8 ? T.SH : T.BASE; }
      layers.push(r);
      break;
    }
    case "bunkbed": {
      // black tube bunk: four posts, two decks, a guard rail up top and a ladder on its right side
      const F = frame(f);
      const metal = ramp(f.color, ["#c6ff00"]);
      const posts: [number, number][] = [[0.04, 0.04], [F.W - 0.12, 0.04], [0.04, F.D - 0.12], [F.W - 0.12, F.D - 0.12]];
      const far = posts.reduce((a, b) => (F.near(a[0] + 0.04, a[1] + 0.04) < F.near(b[0] + 0.04, b[1] + 0.04) ? a : b));
      const post = (p: [number, number]) => { const l = L(metal); F.lbox(l, p[0], p[1], 0.08, 0.08, 0, 46); return l; };
      const deck = (z: number, duvet: string) => {
        const k = L(metal);
        F.lbox(k, 0.06, 0.06, F.W - 0.12, F.D - 0.12, z, 3);
        const m = L(ramp("#f4f4f6"));
        F.lbox(m, 0.1, 0.1, F.W - 0.2, F.D - 0.2, z + 3, 3);
        const dv = L(ramp(duvet));
        F.lbox(dv, 0.08, 0.7, F.W - 0.16, F.D - 0.8, z + 2, 5);
        const pl = L(ramp("#e8e8f0"));
        F.lbox(pl, 0.2, 0.18, F.W - 0.4, 0.4, z + 6, 2.5);
        return F.back ? [k, m, pl, dv] : [k, m, dv, pl];
      };
      // guard rails on both long sides of the top bunk; the far one goes behind the mattress
      const rail = (u: number) => { const l = L(metal); F.lbox(l, u, 0.08, 0.06, F.D - 0.16, 36, 2); return l; };
      const [railFar, railNear] = F.rightSide ? [rail(0.04), rail(F.W - 0.1)] : [rail(F.W - 0.1), rail(0.04)];
      const ladder = L(metal);
      for (const v of [F.D - 0.75, F.D - 0.35]) F.lbox(ladder, F.W - 0.06, v, 0.06, 0.06, 0, 38);
      for (const z of [9, 17, 25, 33]) F.lbox(ladder, F.W - 0.06, F.D - 0.75, 0.06, 0.46, z, 1);
      F.side(ladder, F.W, F.D - 0.62, F.D - 0.48, 37, 38, T.A); // grip tape on the top step
      const rest = posts.filter((p) => p !== far).map(post);
      layers.push(post(far), ...(F.rightSide ? [] : [ladder]), ...deck(0, "#4a4a58"), railFar, ...deck(26, "#c6ff00"), railNear, ...rest, ...(F.rightSide ? [ladder] : []));
      break;
    }
    case "nightstand": {
      // two-drawer nightstand, alarm clock and a soda on top
      const F = frame(f);
      const b = L(ramp(f.color, ["#e8e8f0"]));
      F.lbox(b, 0.15, 0.2, 0.7, 0.6, 0, 14);
      if (!F.back) {
        F.face(b, 0.8, 0.18, 0.82, 7, 7.7, T.DEEP);
        for (const z of [3.5, 10.5]) F.face(b, 0.8, 0.42, 0.58, z, z + 1, T.A);
      } else F.face(b, 0.2, 0.22, 0.78, 2, 12, T.SH);
      layers.push(b);
      const clock = L(ramp("#1e1e24", ["#c6ff00"]));
      F.lbox(clock, 0.22, 0.3, 0.4, 0.22, 14, 6);
      if (!F.back) F.face(clock, 0.52, 0.27, 0.57, 15.5, 18.8, T.A);
      const can = L(ramp("#d0283a", ["#f4f4f6"]));
      F.lbox(can, 0.64, 0.42, 0.13, 0.13, 14, 4);
      layers.push(...(F.near(0.42, 0.42) < F.near(0.7, 0.48) ? [clock, can] : [can, clock]));
      break;
    }
    case "dresser": {
      // chest of drawers, a boombox and a stack of folded tees on top
      const F = frame(f);
      const b = L(ramp(f.color, ["#26262e"]));
      F.lbox(b, 0.06, 0.2, F.W - 0.12, 0.65, 0, 20);
      if (!F.back) {
        const v = 0.85;
        for (const z of [7, 13.5]) F.face(b, v, 0.08, F.W - 0.08, z, z + 0.7, T.DEEP);
        F.face(b, v, F.W / 2 - 0.02, F.W / 2 + 0.02, 14, 19.5, T.DEEP);
        for (const z of [3.5, 10]) F.face(b, v, F.W / 2 - 0.2, F.W / 2 + 0.2, z, z + 1, T.A);
        for (const u of [F.W / 4, (3 * F.W) / 4]) F.face(b, v, u - 0.12, u + 0.12, 16.5, 17.5, T.A);
        F.face(b, v, 0.06, F.W - 0.06, 0, 1.2, T.SH);
      } else F.face(b, 0.2, 0.15, F.W - 0.15, 2, 18, T.SH);
      layers.push(b);
      const box1 = L(ramp("#26262e", ["#9a9aac", "#c6ff00"]));
      F.lbox(box1, 0.18, 0.3, 0.8, 0.36, 20, 7);
      if (!F.back) {
        for (const u of [0.36, 0.8]) { const [px, py] = F.at(u, 0.66, 23.5); box1.ellipse(px - 2, py - 2, px + 1, py + 1, T.A); box1.set(px - 1, py - 1, T.INK); }
        F.face(box1, 0.66, 0.5, 0.66, 25, 26, T.B);
      }
      F.lbox(box1, 0.3, 0.4, 0.56, 0.08, 27, 1.2); // carry handle
      const tees = ["#f4f4f6", "#c6ff00", "#1e1e24"].map((c, i) => { const l = L(ramp(c)); F.lbox(l, F.W - 0.72, 0.32 + i * 0.02, 0.48, 0.4, 20 + i * 2, 2); return l; });
      layers.push(...(F.near(0.58, 0.48) < F.near(F.W - 0.48, 0.52) ? [box1, ...tees] : [...tees, box1]));
      break;
    }
    case "wardrobe": {
      // tall two-door wardrobe, louvered up top, stickers on one door
      const F = frame(f);
      const b = L(ramp(f.color, ["#c6ff00", "#ff3d8b", "#f4f4f6"]));
      F.lbox(b, 0.04, 0.14, F.W - 0.08, 0.76, 0, 46);
      F.lbox(b, 0.01, 0.1, F.W - 0.02, 0.84, 46, 2.5, T.HI);
      if (!F.back) {
        const v = 0.9, m = F.W / 2;
        F.face(b, v, m - 0.02, m + 0.02, 2, 45, T.DEEP);
        F.face(b, v, 0.04, F.W - 0.04, 0, 2, T.DEEP);
        for (const [u0, u1] of [[0.14, m - 0.1], [m + 0.1, F.W - 0.14]]) for (let z = 30; z <= 41; z += 2.6) F.face(b, v, u0, u1, z, z + 1, T.INK);
        for (const u of [m - 0.13, m + 0.09]) F.face(b, v, u, u + 0.04, 18, 25, T.C);
        // stickers: a lime square, a pink dot, a white strip
        F.face(b, v, m + 0.32, m + 0.52, 13, 17, T.A);
        const [px, py] = F.at(m + 0.72, v, 21);
        b.ellipse(px - 2, py - 2, px + 1, py + 1, T.B);
        F.face(b, v, m + 0.26, m + 0.66, 8, 9.4, T.C);
      } else {
        F.face(b, 0.14, 0.1, F.W - 0.1, 2, 44, T.SH);
        for (const u of [F.W / 3, (2 * F.W) / 3]) F.face(b, 0.14, u - 0.02, u + 0.02, 2, 44, T.DEEP);
      }
      layers.push(b);
      break;
    }
    case "desk": {
      // drawer pedestal and two legs under a long top; a see-through Y2K computer and a speaker on it
      const F = frame(f);
      const base = L(ramp(f.color, ["#26262e"]));
      F.lbox(base, 0.08, 0.14, 0.56, 0.72, 0, 14);
      for (const v of F.back ? [0.76, 0.16] : [0.16, 0.76]) F.lbox(base, F.W - 0.18, v, 0.08, 0.08, 0, 14);
      if (!F.back) {
        for (const z of [5, 10]) F.face(base, 0.86, 0.1, 0.62, z, z + 0.6, T.DEEP);
        for (const z of [2.5, 7.5, 12]) F.face(base, 0.86, 0.28, 0.44, z, z + 0.9, T.A);
      } else F.face(base, 0.14, 0.2, F.W - 0.2, 6, 13, T.SH); // modesty panel
      const topL = L(ramp(f.color));
      F.lbox(topL, 0.02, 0.08, F.W - 0.04, 0.84, 14, 2);
      layers.push(base, topL);
      const mon = L(ramp("#3fb8c8", ["#14202c", "#c6ff00", "#f4f4f6"]));
      F.lbox(mon, 0.32, 0.2, 0.66, 0.5, 16, 15);
      if (!F.back) {
        F.face(mon, 0.7, 0.4, 0.9, 19.5, 29, T.A);
        for (const [u1, z] of [[0.72, 26.5], [0.6, 24.5], [0.8, 22.5]] as const) F.face(mon, 0.7, 0.45, u1, z, z + 0.8, T.B);
      } else {
        F.lbox(mon, 0.42, 0.1, 0.46, 0.1, 19, 10); // the tube's back hump
        F.face(mon, 0.1, 0.5, 0.8, 23, 23.8, T.DEEP);
      }
      const kb = L(ramp("#e8e8f0"));
      F.lbox(kb, 0.34, 0.74, 0.64, 0.12, 16, 1);
      const spk = L(ramp("#26262e", ["#9a9aac"]));
      F.lbox(spk, F.W - 0.52, 0.26, 0.22, 0.22, 16, 7);
      if (!F.back) { const [px, py] = F.at(F.W - 0.41, 0.48, 19); spk.ellipse(px - 1, py - 1, px + 1, py + 1, T.A); }
      const parts: [number, Layer][] = [[F.near(0.65, 0.45), mon], [F.near(0.66, 0.8), kb], [F.near(F.W - 0.41, 0.37), spk]];
      layers.push(...parts.sort((a, b) => a[0] - b[0]).map((p) => p[1]));
      break;
    }
    case "deskchair": {
      // gaming chair: cross base, gas lift, seat, arms, a tall back with lime racing stripes
      const F = frame(f);
      const pal = ramp(f.color, ["#c6ff00", "#5a5a68"]);
      const base = L(pal);
      F.lbox(base, 0.12, 0.45, 0.76, 0.1, 0, 1.5);
      F.lbox(base, 0.45, 0.12, 0.1, 0.76, 0, 1.5);
      F.lbox(base, 0.46, 0.46, 0.08, 0.08, 1.5, 8, T.B);
      const seat = L(pal);
      F.lbox(seat, 0.18, 0.28, 0.64, 0.58, 9.5, 3);
      const arms = [0.12, 0.8].map((u) => { const l = L(pal); F.lbox(l, u, 0.38, 0.08, 0.36, 12.5, 4); return l; });
      if (!F.rightSide) arms.reverse();
      const back = L(pal);
      F.lbox(back, 0.18, 0.14, 0.64, 0.14, 12.5, 19);
      F.lbox(back, 0.3, 0.16, 0.4, 0.1, 31.5, 4); // headrest
      const v = F.back ? 0.14 : 0.28;
      for (const u of [0.34, 0.6]) F.face(back, v, u, u + 0.06, 14, 30, T.A);
      if (F.back) F.face(back, v, 0.42, 0.58, 24, 27, T.A);
      layers.push(base, ...(F.back ? [seat, ...arms, back] : [back, seat, ...arms]));
      break;
    }
    case "checkrug": {
      // black and white skater checks with a black border
      const r = L(ramp("#f4f4f6", [f.color]));
      r.poly(diamond(x + 0.05, y + 0.05, w - 0.1, h - 0.1), T.A);
      const n = (w - 0.5) * 2, m = (h - 0.5) * 2;
      for (let i = 0; i < n; i++) for (let j = 0; j < m; j++) r.poly(diamond(x + 0.25 + i * 0.5, y + 0.25 + j * 0.5, 0.5, 0.5), (i + j) % 2 ? T.A : T.BASE);
      layers.push(r);
      break;
    }
    // ---- 90s bedroom
    case "waterbed": {
      // a waterbed in a padded oak surround, a bookcase headboard full of tapes, a Memphis-print comforter
      const F = frame(f);
      const { W, D } = F;
      const bd = Object.values(BEDDING).find((b) => b.base === f.color) ?? BEDDING.memphis;
      const oak = ramp("#c8955a", bd.books);
      const ped = L(oak);
      F.lbox(ped, 0.06, 0.34, W - 0.12, D - 0.4, 0, 6);
      F.face(ped, F.back ? 0.34 : D - 0.06, 0.06, W - 0.06, 0, 1.5, T.DEEP); // toe kick
      const head = L(oak);
      F.lbox(head, 0, 0, W, 0.34, 0, 32);
      F.lbox(head, -0.02, -0.02, W + 0.04, 0.38, 32, 2, T.HI);
      if (!F.back) {
        // three cubbies over the pillows, tapes and books standing in them
        const cw = (W - 0.24) / 3;
        for (let i = 0; i < 3; i++) {
          const u0 = 0.09 + i * (cw + 0.03);
          F.face(head, 0.34, u0, u0 + cw, 18, 29, T.INK);
          const tones = [T.A, T.B, T.C, T.W, T.B, T.A];
          for (let k = 0; k < 5; k++) {
            const a = u0 + 0.04 + k * 0.1;
            if ((i + k) % 4 === 3) continue;
            F.face(head, 0.34, a, a + 0.07, 18, 25 + ((i * 3 + k) % 3), tones[(i * 2 + k) % 6]);
          }
        }
      } else {
        for (let i = 1; i < 4; i++) F.face(head, 0, (i * W) / 4 - 0.02, (i * W) / 4 + 0.02, 3, 30, T.DEEP);
      }
      // the padded rails round the water mattress
      const rail = (u0: number, v0: number, du: number, dv: number) => { const l = L(oak); F.lbox(l, u0, v0, du, dv, 6, 8); F.lbox(l, u0, v0, du, dv, 14, 1, T.HI); return { l, n: F.near(u0 + du / 2, v0 + dv / 2) }; };
      const rails = [rail(0.06, 0.34, 0.14, D - 0.4), rail(W - 0.2, 0.34, 0.14, D - 0.4), rail(0.2, D - 0.2, W - 0.4, 0.14)];
      const water = L(ramp("#3a6ad8", ["#7ad8ff"]));
      F.lbox(water, 0.2, 0.34, W - 0.4, D - 0.54, 6, 7);
      const sheet = L(ramp("#f4f4f6"));
      F.lbox(sheet, 0.2, 0.36, W - 0.4, D - 0.58, 13, 1);
      const dv = L(ramp(bd.base, bd.acc));
      F.lbox(dv, 0.16, 1.15, W - 0.32, D - 1.33, 12, 4.5);
      bd.draw(dv, F, W, D);
      const fold = L(ramp(bd.fold));
      F.lbox(fold, 0.16, 1.02, W - 0.32, 0.3, 12, 5);
      const pillows = F.seq(2).map((i) => { const l = L(ramp(bd.pillows[i])); F.lbox(l, i ? W - 0.92 : 0.22, 0.42, 0.7, 0.52, 14, 3.5); return l; });
      const covers = F.back ? [dv, fold, ...pillows] : [...pillows, dv, fold];
      const mid = F.near(W / 2, D / 2);
      const farRails = rails.filter((r) => r.n < mid).map((r) => r.l), nearRails = rails.filter((r) => r.n >= mid).map((r) => r.l);
      const body = [ped, ...farRails, water, sheet, ...covers, ...nearRails];
      layers.push(...(F.back ? [...body, head] : [head, ...body]));
      break;
    }
    case "futon": {
      // a futon on a black tube frame, the mattress folded up into a couch with tufts
      const F = frame(f);
      const { W } = F;
      const metal = ramp("#26262e", ["#5a5a68"]);
      const cush = ramp(f.color);
      const arm = (u: number) => {
        const l = L(metal);
        F.lbox(l, u, 0.12, 0.08, 0.08, 0, 22);
        F.lbox(l, u, 0.82, 0.08, 0.08, 0, 13);
        F.lbox(l, u, 0.12, 0.08, 0.78, 12, 1.5);
        F.lbox(l, u, 0.12, 0.08, 0.78, 3, 1.5);
        return l;
      };
      const [armL, armR] = [arm(0.02), arm(W - 0.1)];
      const [farArm, nearArm] = F.rightSide ? [armL, armR] : [armR, armL];
      const seat = L(cush);
      F.lbox(seat, 0.1, 0.32, W - 0.2, 0.6, 5, 6);
      const back = L(cush);
      F.lbox(back, 0.1, 0.1, W - 0.2, 0.24, 5, 20);
      const n = Math.round(W * 2);
      for (let i = 0; i < n; i++) {
        const u = 0.1 + ((i + 0.5) * (W - 0.2)) / n;
        if (!F.back) {
          seat.set(...F.at(u, 0.62, 11), T.DEEP);
          for (const z of [12, 20]) back.set(...F.at(u, 0.34, z), T.DEEP);
          seat.set(...F.at(u, 0.92, 8), T.DEEP);
        } else for (const z of [12, 20]) back.set(...F.at(u, 0.1, z), T.DEEP);
      }
      const bar = L(metal);
      F.lbox(bar, 0.06, 0.06, W - 0.12, 0.05, 9, 1.5);
      F.lbox(bar, 0.06, 0.06, W - 0.12, 0.05, 20, 1.5);
      layers.push(farArm, ...(F.back ? [seat, back, bar] : [bar, back, seat]), nearArm);
      break;
    }
    case "papasan": {
      // a round rattan bowl chair on a drum base, a thick round cushion in it
      const d = f.dir ?? 0;
      const [px, py] = c(x + 0.5, y + 0.5);
      const rat = ramp("#c8964f", ["#8a5a2a"]);
      const base = L(rat);
      base.ellipse(px - 7, py - 6, px + 6, py + 1, T.SH);
      base.rect(px - 7, py - 10, 14, 6, T.SH);
      base.ellipse(px - 7, py - 13, px + 6, py - 7, T.BASE);
      // the bowl is a wide dish tipped back; facing us the cushion fills it, from behind only its top shows over the rim
      const [fx, fy] = ([[-2, 1], [2, 1], [2, -1], [-2, -1]] as const)[d];
      const bowl = L(rat);
      bowl.ellipse(px - 15, py - 32, px + 14, py - 10, T.BASE);
      for (let yy = 0; yy < RH; yy++) for (let xx = 0; xx < RW; xx++) if (bowl.get(xx, yy) && (xx + yy * 2) % 4 === 0) bowl.set(xx, yy, T.SH);
      const cush = L(ramp(f.color));
      if (d < 2) {
        cush.ellipse(px - 12 + fx, py - 29 + fy, px + 11 + fx, py - 13 + fy, T.BASE);
        cush.shade({ right: 2, bottom: 2 });
        cush.set(px - 1 + fx, py - 21 + fy, T.DEEP).set(px + fx, py - 21 + fy, T.DEEP);
        layers.push(base, bowl, cush);
      } else {
        cush.ellipse(px - 11 + fx, py - 35, px + 10 + fx, py - 22, T.BASE);
        layers.push(base, cush, bowl);
      }
      break;
    }
    case "gamestand": {
      // low TV stand: a chunky grey TV playing a platformer, a VCR on the shelf, a game console and its pad on top
      const F = frame(f);
      const { W } = F;
      const st = L(ramp(f.color, ["#c4c8d4", "#3cff8a", "#1e1e24"]));
      F.lbox(st, 0.04, 0.2, W - 0.08, 0.7, 0, 11);
      F.lbox(st, 0.02, 0.18, W - 0.04, 0.74, 11, 1.5, T.HI);
      if (!F.back) {
        F.face(st, 0.9, 0.12, W - 0.12, 2, 9.5, T.INK); // open shelf
        F.face(st, 0.9, 0.2, 1.1, 2.5, 6, T.A); // VCR
        F.face(st, 0.9, 0.26, 0.7, 4, 5, T.C);
        st.set(...F.at(0.9, 0.9, 5), T.B).set(...F.at(0.96, 0.9, 5), T.B);
        F.face(st, 0.9, 1.2, 1.7, 2.5, 4, T.SH); // games stacked
        F.face(st, 0.9, 1.25, 1.65, 4, 5.5, T.DEEP);
      } else F.face(st, 0.2, 0.12, W - 0.12, 2, 9.5, T.SH);
      const tv = L(ramp("#8a8a96", ["#2a3a8a", "#3fae4a", "#ff3d3d", "#ffd23f"]));
      F.lbox(tv, 0.24, 0.3, 0.96, 0.58, 12.5, 18);
      const hump = L(tv.pal);
      if (!F.back) {
        F.face(tv, 0.88, 0.32, 1.12, 16, 28.5, T.A);
        F.face(tv, 0.88, 0.32, 1.12, 16, 19, T.B); // ground
        F.face(tv, 0.88, 0.75, 0.95, 21, 22.5, T.B); // a floating block
        F.face(tv, 0.88, 0.46, 0.54, 19, 22, T.C); // the hero
        F.face(tv, 0.88, 0.94, 1.0, 23.5, 25, T.W); // a coin
        tv.pal[T.W] = hex("#ffd23f");
        F.face(tv, 0.88, 0.32, 0.4, 13.5, 14.5, T.HI);
      } else {
        F.lbox(hump, 0.4, 0.14, 0.64, 0.16, 14, 13);
        for (const z of [19, 22]) F.face(hump, 0.14, 0.48, 0.96, z, z + 0.6, T.DEEP);
      }
      const con = L(ramp("#9a9aa6", ["#8a5ad8", "#ff3d8b", "#1e1e24"]));
      F.lbox(con, 1.36, 0.34, 0.5, 0.4, 12.5, 3);
      F.lbox(con, 1.48, 0.42, 0.22, 0.2, 15.5, 0.8, T.SH); // cartridge slot
      if (!F.back) { F.face(con, 0.74, 1.42, 1.5, 13.5, 14.5, T.B); F.face(con, 0.74, 1.56, 1.62, 13.5, 14.5, T.A); }
      const pad = L(ramp("#9a9aa6", ["#8a5ad8", "#ff3d8b", "#1e1e24"]));
      F.lbox(pad, 1.42, 0.78, 0.32, 0.12, 12.5, 1.5);
      pad.set(...F.at(1.5, 0.84, 14), T.C).set(...F.at(1.66, 0.84, 14), T.B);
      const parts: [number, Layer][] = [[F.near(0.72, 0.59), tv], [F.near(1.6, 0.54), con], [F.near(1.58, 0.84), pad]];
      layers.push(st, ...(F.back ? [hump] : []), ...parts.sort((a, b) => a[0] - b[0]).map((p) => p[1]));
      break;
    }
    case "hifi": {
      // a stacked stereo: tape deck, CD player, a lit equalizer, a record player under a smoked lid
      const F = frame(f);
      const s = L(ramp(f.color, ["#c4c8d4", "#c6ff00", "#ff3d8b", "#21d4fd"]));
      F.lbox(s, 0.2, 0.24, 0.6, 0.52, 0, 2, T.SH);
      F.lbox(s, 0.16, 0.2, 0.68, 0.6, 2, 32);
      const v = 0.8;
      if (!F.back) {
        for (const z of [10, 17, 24]) F.face(s, v, 0.18, 0.82, z, z + 0.6, T.INK);
        // tape deck: two windows
        for (const u of [0.24, 0.54]) F.face(s, v, u, u + 0.22, 4, 8, T.A);
        // CD player: tray and a little display
        F.face(s, v, 0.22, 0.6, 13, 14, T.A);
        F.face(s, v, 0.66, 0.78, 12.5, 14.5, T.B);
        // equalizer bars
        const bars = [3, 5, 4, 6, 3, 5, 2];
        bars.forEach((hh, i) => { const u = 0.24 + i * 0.075; F.face(s, v, u, u + 0.045, 19, 19 + hh * 0.7, i % 3 === 0 ? T.C : i % 3 === 1 ? T.B : T.W); });
        s.pal[T.W] = hex("#21d4fd");
        F.face(s, v, 0.22, 0.4, 27, 30, T.A);
        s.set(...F.at(0.62, v, 29), T.B).set(...F.at(0.7, v, 29), T.C);
      } else {
        F.face(s, 0.2, 0.22, 0.78, 4, 30, T.SH);
        for (const z of [8, 16, 24]) F.face(s, 0.2, 0.3, 0.7, z, z + 0.6, T.DEEP);
      }
      const lid = L(ramp("#5a5a68", ["#1e1e24"]));
      F.lbox(lid, 0.18, 0.22, 0.64, 0.56, 34, 3);
      const [rx, ry] = F.at(0.5, 0.5, 37);
      lid.ellipse(rx - 5, ry - 2, rx + 4, ry + 2, T.A);
      lid.set(rx, ry, T.HI);
      layers.push(s, lid);
      break;
    }
    case "tapeshelf": {
      // a tall shelf packed with tapes in their sleeves and cassettes
      const F = frame(f);
      const s = L(ramp(f.color, ["#1e1e24", "#f4f4f6", "#ff5fa2", "#21d4fd"]));
      F.lbox(s, 0.18, 0.3, 0.64, 0.5, 0, 42);
      F.lbox(s, 0.16, 0.28, 0.68, 0.54, 42, 1.5, T.HI);
      const v = 0.8;
      if (!F.back) {
        const rows = [2, 12, 22, 32];
        rows.forEach((z0, r) => {
          F.face(s, v, 0.22, 0.78, z0, z0 + 8.5, T.INK);
          for (let k = 0; k < 7; k++) {
            const u = 0.24 + k * 0.077;
            if ((k + r) % 5 === 4) continue; // a gap
            const tall = r < 2;
            const tone = tall ? T.A : [T.B, T.C, T.W][(k + r) % 3];
            F.face(s, v, u, u + 0.06, z0, z0 + (tall ? 7.5 : 4.5), tone);
            if (tall) F.face(s, v, u + 0.01, u + 0.05, z0 + 4, z0 + 5, T.B); // label
          }
        });
        s.pal[T.W] = hex("#ffd23f");
      } else F.face(s, 0.3, 0.22, 0.78, 2, 40, T.SH);
      layers.push(s);
      break;
    }
    case "fishtank": {
      // a fish tank on a black stand: neon gravel, weeds, a castle, three fish and some bubbles
      const F = frame(f);
      const { W } = F;
      const stand = L(ramp("#26262e", ["#5a5a68"]));
      F.lbox(stand, 0.06, 0.24, W - 0.12, 0.56, 0, 15);
      if (!F.back) { F.face(stand, 0.8, W / 2 - 0.02, W / 2 + 0.02, 2, 14, T.DEEP); for (const u of [W / 2 - 0.12, W / 2 + 0.08]) F.face(stand, 0.8, u, u + 0.04, 8, 10, T.A); }
      const glass = ramp("#8ad8f0", ["#ff5fa2", "#3fae4a", "#ff7a1a", "#f4f4f6"]);
      glass[T.SH] = hex("#5ab8e0");
      glass[T.DEEP] = hex("#3a90c8");
      const t = L(glass);
      F.lbox(t, 0.06, 0.22, W - 0.12, 0.6, 15, 18);
      const v = F.back ? 0.22 : 0.82;
      F.face(t, v, 0.06, W - 0.06, 15, 18, T.A); // gravel
      F.side(t, F.rightSide ? W - 0.06 : 0.06, 0.22, 0.82, 15, 18, T.A);
      F.top(t, 0.06, 0.22, W - 0.06, 0.82, 33, T.HI);
      // weeds
      for (const [u, hh] of [[0.24, 11], [0.34, 8], [W - 0.4, 12], [W - 0.3, 7]] as const)
        for (let z = 18; z < 18 + hh; z++) { const [px, py] = F.at(u, v, z); t.set(px + ((z >> 1) % 2), py, T.B); }
      // castle
      F.face(t, v, W / 2 + 0.1, W / 2 + 0.34, 18, 23, T.DEEP);
      for (const u of [W / 2 + 0.1, W / 2 + 0.26]) F.face(t, v, u, u + 0.08, 23, 24.5, T.DEEP);
      // fish
      for (const [u, z] of [[0.55, 27], [1.0, 22], [1.35, 29]] as const) {
        const [px, py] = F.at(u, v, z);
        t.rect(px, py, 3, 2, T.C).set(px + (F.back ? 3 : -1), py, T.C).set(px + (F.back ? 3 : -1), py + 1, T.C).set(px + (F.back ? 0 : 2), py, T.INK);
      }
      for (const [u, z] of [[0.7, 25], [0.72, 28], [0.69, 31]] as const) t.set(...F.at(u, v, z), T.W);
      const lid = L(ramp("#26262e"));
      F.lbox(lid, 0.04, 0.2, W - 0.08, 0.64, 33, 2);
      layers.push(stand, t, lid);
      break;
    }
    case "plasma": {
      // a plasma ball on a little black stand, purple lightning crawling to the glass
      const [px, py] = c(x + 0.5, y + 0.5);
      const F = frame(f);
      const st = L(ramp("#26262e", ["#5a5a68"]));
      F.lbox(st, 0.28, 0.28, 0.44, 0.44, 0, 12);
      st.ellipse(px - 4, py - 18, px + 3, py - 13, T.BASE);
      const g = L(ramp("#2a1048", [f.color, "#ffffff", "#ff8fe0"]));
      g.ellipse(px - 9, py - 35, px + 8, py - 18, T.BASE);
      const cx = px, cy = py - 27;
      // lightning: wiggly strands from the core out to the glass
      for (const ang of [0.3, 1.2, 2.2, 3.0, 3.9, 4.8, 5.6]) {
        for (let r = 2; r <= 8; r++) {
          const a = ang + Math.sin(r * 1.7 + ang * 3) * 0.25;
          const sx = Math.round(cx + Math.cos(a) * r), sy = Math.round(cy + Math.sin(a) * r);
          if (g.get(sx, sy)) g.set(sx, sy, r === 8 ? T.C : T.A);
        }
      }
      g.ellipse(cx - 2, cy - 2, cx + 1, cy + 1, T.B);
      g.set(px - 5, py - 32, T.HI).set(px - 6, py - 31, T.HI);
      layers.push(st, g);
      break;
    }
    case "memphisrug": {
      // a Memphis-print rug: squiggles, triangles and confetti on black, a teal border
      const r = L(ramp(f.color, ["#2fb8a8", "#ff5fa2", "#ffd23f", "#8a5ad8"]), null);
      r.poly(diamond(x + 0.05, y + 0.05, w - 0.1, h - 0.1), T.A);
      r.poly(diamond(x + 0.2, y + 0.2, w - 0.4, h - 0.4), T.BASE);
      const inside = (px: number, py: number) => r.get(px, py) === T.BASE;
      let i = 0;
      for (let a = 0.45; a < w - 0.3; a += 0.55) for (let b = 0.4; b < h - 0.3; b += 0.5) {
        const [px, py] = ip(x + a, y + b).map(Math.round);
        const kind = (i++ * 5 + Math.round(a * 3)) % 4;
        if (kind === 0) for (let k = 0; k < 7; k++) { const sx = px - 3 + k, sy = py + (k % 4 < 2 ? 0 : 1); if (inside(sx, sy)) r.set(sx, sy, T.B); } // squiggle
        else if (kind === 1) { for (const [dx, dy] of [[0, -2], [-1, -1], [0, -1], [1, -1], [-2, 0], [-1, 0], [0, 0], [1, 0], [2, 0]]) if (inside(px + dx, py + dy)) r.set(px + dx, py + dy, T.C); } // triangle
        else if (kind === 2) { for (const [dx, dy] of [[-1, 0], [0, 0], [1, 0], [0, -1], [0, 1]]) if (inside(px + dx, py + dy)) r.set(px + dx, py + dy, T.W); } // a dot
        else for (let k = 0; k < 4; k++) if (inside(px - 2 + k, py - 1 + k)) r.set(px - 2 + k, py - 1 + k, T.A); // a dash
      }
      r.pal[T.W] = hex("#8a5ad8");
      layers.push(r);
      break;
    }
    default:
      return null;
  }
  const full = composite(RW, RH, layers);
  const r = bounds(full);
  if (!r) return null;
  return { bmp: crop(full, r), x: r.x - OX, y: r.y - OY };
}

/** 1px white outline of a tile, for the hover cursor. */
export function buildHover(): FurniArt {
  const d = new Layer(RW, RH, ramp("#ffffff"), null);
  d.poly(diamond(0, 0, 1, 1), T.BASE);
  const inner = new Layer(RW, RH, d.pal, null);
  inner.poly(diamond(0.1, 0.1, 0.8, 0.8), T.BASE);
  for (let i = 0; i < d.data.length; i++) if (inner.data[i]) d.data[i] = 0;
  const full = composite(RW, RH, [d]);
  const r = bounds(full)!;
  return { bmp: crop(full, r), x: r.x - OX, y: r.y - OY };
}
